/**
 * Avatar Configuration and Mapping
 * Maps frontend avatar IDs to their display information and RAG sahabe identifiers
 *
 * Images are served from CloudFront CDN at /avatars/{avatarId}/images/{filename}
 * This eliminates webpack bundling/hashing and provides consistent URLs for web + mobile
 */

// CDN base URL - uses CloudFront distribution
// In production, this comes from the same domain as the app
// Format: /avatars/{avatarId}/images/{snake_case_filename}.jpg
const CDN_BASE = '/avatars';

// Toggle this to true AFTER you upload optimized images (_thumb.webp, _medium.webp, etc.)
const USE_OPTIMIZED_IMAGES = true;

// Image size variants for responsive loading
export type ImageSize = 'thumb' | 'medium' | 'large' | 'full';

export interface ResponsiveImage {
  thumb: string; // ~300px width, WebP (~7KB) - fast initial load
  thumbFallback: string;
  medium: string; // ~600px width, WebP (~25KB) - side cards
  mediumFallback: string;
  large: string; // ~900px width, WebP (~50KB) - active/spotlight card
  largeFallback: string;
  full: string; // Original size, JPG
}

// Helper to build CDN image URL
const buildImageUrl = (avatarId: string, filename: string): string => {
  return `${CDN_BASE}/${avatarId}/images/${filename}`;
};

// Helper to build responsive image URLs (only returns values if optimized images are enabled)
const buildResponsiveImageUrls = (
  avatarId: string,
  baseFilename: string,
): ResponsiveImage | undefined => {
  // Return undefined if optimized images are not uploaded yet
  if (!USE_OPTIMIZED_IMAGES) return undefined;

  // Extract name without extension
  const nameWithoutExt = baseFilename.replace(/\.[^/.]+$/, '');

  return {
    thumb: `${CDN_BASE}/${avatarId}/images/${nameWithoutExt}_thumb.webp`,
    thumbFallback: `${CDN_BASE}/${avatarId}/images/${nameWithoutExt}_thumb.jpg`,
    medium: `${CDN_BASE}/${avatarId}/images/${nameWithoutExt}_medium.webp`,
    mediumFallback: `${CDN_BASE}/${avatarId}/images/${nameWithoutExt}_medium.jpg`,
    large: `${CDN_BASE}/${avatarId}/images/${nameWithoutExt}_large.webp`,
    largeFallback: `${CDN_BASE}/${avatarId}/images/${nameWithoutExt}_large.jpg`,
    full: `${CDN_BASE}/${avatarId}/images/${baseFilename}`,
  };
};

// Helper to build CDN video URL
const buildVideoUrl = (avatarId: string, filename: string): string => {
  return `${CDN_BASE}/${avatarId}/videos/${filename}`;
};

// Default HeyGen avatar ID for streaming (Bilal Habeshi Interactive Avatar)
const DEFAULT_HEYGEN_AVATAR_ID = 'a534027b-cdc1-4122-b0e4-951a78ebcb39';

// Default video assets (Bilal is the default avatar)
const bilalIdleVideo = buildVideoUrl('bilal', 'bilal_idle.mp4');

export interface AvatarInfo {
  id: string;
  name: string; // Display Name (Turkish)
  name_en: string; // English Name with (RA)
  name_ar?: string; // Arabic Name with honorific
  name_tr?: string; // Turkish Name with Hz. prefix
  shortName?: string; // Common known name
  fullName?: string; // Full formal name
  gender?: 'male' | 'female'; // Determines RA vs RA honorific
  image: string;
  images?: ResponsiveImage; // Responsive image variants (WebP + fallbacks)
  sahabeName: string;
  heygenAvatarId?: string; // HeyGen streaming avatar ID
  heygenLiveAvatarId?: string; // HeyGen Live Avatar ID for streaming
  introVideo?: string; // Path to intro video
  idleVideo?: string; // Path to idle/loop video
  description?: string;
  description_tr?: string; // Turkish description
  description_ar?: string; // Arabic description
  era_en?: string;
  era_tr?: string;
  title?: string; // e.g. "Fourth Caliph" (New)
  title_tr?: string; // Turkish title
  title_ar?: string; // Arabic title
  tags?: string[]; // e.g. ["Prophet's Cousin"] (New)
  tags_tr?: string[]; // Turkish tags
  tags_ar?: string[]; // Arabic tags
  roles?: string[]; // e.g. ["First Caliph", "Close Companion"]
  lifespan?: string; // e.g. "573-634 CE"
  // Video support
  video?: string;
  // Computed getters
  englishName?: string;
  arabicName?: string;
}

// Helper to get display-ready avatar info
export function getDisplayAvatarInfo(
  avatar: AvatarInfo,
): AvatarInfo & { englishName: string; arabicName: string; roles: string[]; lifespan: string } {
  return {
    ...avatar,
    englishName: avatar.name_en || avatar.name,
    arabicName: avatar.name_ar || '',
    roles: avatar.roles || avatar.tags || ['Companion'],
    lifespan: avatar.lifespan || avatar.era_en || 'Unknown',
  };
}

export interface LocalizedAvatarInfo extends AvatarInfo {
  displayName: string;
  displayDescription: string;
  displayTitle: string;
  displayTags: string[];
  arabicName: string;
  lifespan: string;
}

export function getLocalizedAvatarInfo(avatar: AvatarInfo, language: string): LocalizedAvatarInfo {
  const lang = language.split('-')[0];

  const displayName =
    lang === 'tr' && avatar.name_tr
      ? avatar.name_tr
      : lang === 'ar' && avatar.name_ar
        ? avatar.name_ar
        : avatar.name_en || avatar.name;

  const displayDescription =
    lang === 'tr' && avatar.description_tr
      ? avatar.description_tr
      : lang === 'ar' && avatar.description_ar
        ? avatar.description_ar
        : avatar.description || '';

  const displayTitle =
    lang === 'tr' && avatar.title_tr
      ? avatar.title_tr
      : lang === 'ar' && avatar.title_ar
        ? avatar.title_ar
        : avatar.title || '';

  const displayTags =
    lang === 'tr' && avatar.tags_tr
      ? avatar.tags_tr
      : lang === 'ar' && avatar.tags_ar
        ? avatar.tags_ar
        : avatar.tags || ['Companion'];

  return {
    ...avatar,
    displayName,
    displayDescription,
    displayTitle,
    displayTags,
    arabicName: avatar.name_ar || '',
    lifespan: avatar.lifespan || (lang === 'tr' ? avatar.era_tr : avatar.era_en) || 'Unknown',
  };
}

/**
 * Complete avatar configuration with RAG integration
 * Images use CDN paths (snake_case filenames)
 */
export const AVATARS: Record<string, AvatarInfo> = {
  'abin-huzafe': {
    id: 'abin-huzafe',
    name: 'Hz. Abdullah b. Huzâfe',
    name_en: 'Abdullah ibn Hudhafah (RA)',
    name_ar: '',
    name_tr: 'Hz. Abdullah b. Huzâfe (RA)',
    shortName: 'Ibn Hudhafah',
    fullName: 'Abdullah ibn Hudhafah al-Sahmi',
    gender: 'male',
    image: buildImageUrl('abin-huzafe', 'a_bin_huzafe_es_sehmi.jpg'),
    images: buildResponsiveImageUrls('abin-huzafe', 'a_bin_huzafe_es_sehmi.jpg'),
    sahabeName: 'abdullah_b_huzafe_es-sehmi_en',
    heygenAvatarId: 'b171463212264480b608c56f44615e41',
    heygenLiveAvatarId: '64110f63-aee3-4000-8ebf-57488d815475',
    description:
      "Though his exact birth date is not known, 'Abdullah ibn Hudhafah was born in Mecca and became known as a bold, sharp-minded envoy chosen by the Messenger of God (peace and blessings be upon him) for the toughest diplomatic missions. He fearlessly delivered the Prophet's letter inviting the Sasanian emperor Kisra to Islam, and he was also known for a witty, lighthearted temperament that brought smiles to those around him. Captured by the Byzantine emperor, he refused to compromise his faith. In one famous episode, he used a clever strategy: in exchange for kissing the emperor's head, he secured the release of 80 Muslim prisoners from certain death. Because of this, 'Umar is reported to have said, \"Every Muslim should kiss his head.\" He later took part in the conquest of Egypt and died there around 33 AH.",
    description_tr:
      "Doğum tarihi kesin olarak bilinmemekle birlikte Mekke'de dünyaya gelen bu güzide sahabi, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) en zorlu diplomatik görevler için seçtiği, zekâsı ve cesaretiyle tanınan özel bir elçidir. Hem Sâsânî İmparatoru Kisrâ'ya İslam'a davet mektubunu korkusuzca götürmesiyle hem de çevresindekileri güldüren şakacı ve nükteci mizacıyla bilinmektedir. O, Bizans İmparatoru tarafından esir alındığında dininden asla taviz vermemiş ve zekice bir hamleyle İmparatorun başını öpmesi karşılığında seksen Müslüman esiri mutlak bir ölümden kurtarmıştır. Bu feraseti sebebiyle Hz. Ömer'in \"Her Müslüman onun başını öpmeli\" iltifatına mazhar olan Abdullah, Mısır'ın fethine de katılmış ve hicri 33 yılı dolaylarında orada vefat etmiştir.",
    description_ar: '',
    era_en: '580~654 CE',
    era_tr: '580~654 MS',
    title: 'Diplomat',
    title_tr: 'Diplomat',
    title_ar: '',
    tags: ['Uncompromising Believer', 'Fearless Diplomat', 'Witty Companion'],
    tags_tr: ['Tavizsiz Mümin', 'Gözüpek Diplomat', 'Nüktedan sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abin-huzafe', 'a_bin_huzafe_es_sehmi_idle.mp4'),
  },
  'abdullah-abbas': {
    id: 'abdullah-abbas',
    name: 'Hz. Abdullah b. Abbâs',
    name_en: 'Abdullah ibn Abbas (RA)',
    name_ar: 'عبد الله بن عباس رضي الله عنه',
    name_tr: 'Hz. Abdullah bin Abbas (RA)',
    shortName: 'Ibn Abbas',
    fullName: 'Abdullah ibn Abbas ibn Abd al-Muttalib',
    gender: 'male',
    image: buildImageUrl('abdullah-abbas', 'abdullah_bin_abbas.jpg'),
    images: buildResponsiveImageUrls('abdullah-abbas', 'abdullah_bin_abbas.jpg'),
    sahabeName: 'abdullah_b_abbas_en',
    heygenAvatarId: '845b6848cbf54cb39121ad5359c905e2',
    heygenLiveAvatarId: 'a719dec1-3ee6-4041-8db0-837fa28a9b3e',
    description:
      "Born in Mecca three years before the Hijrah, Ibn 'Abbas was the Prophet's cousin and one of the greatest scholars among the Companions. As a child he received the Prophet's prayer: \"O God, grant him deep understanding of the religion and teach him the interpretation of the Qur'an.\" Through this blessing he became known as \"the Scholar of the Ummah\" and \"the Interpreter of the Qur'an.\" A gifted speaker, generous, and deeply pious, he devoted his life to teaching and unlocking the Qur'an's meanings. He died in Ta'if in 68 AH, at the age of 71.",
    description_tr:
      "Hicret'ten üç yıl önce Mekke'de dünyaya gelmiş, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) amcasının oğlu olan kıymetli bir sahabidir. Henüz çocukken Efendimiz'in \"Allahım onu dinde derin kavrayış sahibi kıl ve ona Kur'an'ın tefsirini öğret\" duasına mazhar olmuştur. Bu eşsiz dua bereketiyle \"Ümmetin Âlimi\" ve \"Kur'ân'ın Tercümanı\" unvanlarını kazanmış; tefsir, hadis ve fıkıh alanlarında uçsuz bucaksız bir bilgi deryası haline gelmiştir. Hitabeti güçlü, cömert ve takva sahibi bir şahsiyet olarak hayatını Kur'an'ın sırlarını çözmeye ve öğretmeye adamıştır. Asırlar boyu müminlere ışık tutan bu büyük ilim otoritesi, Hicri 68 yılında, 71 yaşındayken Tâif'te fani dünyaya veda etmiştir.",
    description_ar: 'مفسر القرآن',
    era_en: '619~687 CE',
    era_tr: '619~687 MS',
    title: 'Interpreter of Quran',
    title_tr: "Kur'an Müfessiri",
    title_ar: 'مفسر القرآن',
    tags: [
      'Scholar of the Ummah',
      "Interpreter of the Qur'an",
      'Eloquent Orator',
      'Man of Peace',
      'Keeper of Secrets',
    ],
    tags_tr: ['Ümmetin Âlimi', "Kur'ân'ın Tercümanı", 'Fasih Hatip', 'Sulh ehli', 'Sır küpü'],
    tags_ar: ['العالم', 'ابن عم النبي'],
    idleVideo: buildVideoUrl('abdullah-abbas', 'abdullah_bin_abbas_idle.mp4'),
  },
  'abdullah-mesud': {
    id: 'abdullah-mesud',
    name: "Hz. Abdullah b. Mes'ûd",
    name_en: 'Abdullah ibn Masud (RA)',
    name_ar: 'عبد الله بن مسعود رضي الله عنه',
    name_tr: 'Hz. Abdullah bin Mesud (RA)',
    shortName: 'Ibn Masud',
    fullName: 'Abdullah ibn Masud al-Hudhali',
    gender: 'male',
    image: buildImageUrl('abdullah-mesud', 'abdullah_bin_mesud.jpg'),
    images: buildResponsiveImageUrls('abdullah-mesud', 'abdullah_bin_mesud.jpg'),
    sahabeName: 'abdullah_b_mesud_en',
    heygenAvatarId: 'ff6585c94bb4426e92d770619f01c310',
    heygenLiveAvatarId: 'd61e04e0-e021-4761-9fd4-a73ff0dd2555',
    description:
      "One of the earliest pioneers of Islam and among those closest to the Messenger of God (peace and blessings be upon him), Ibn Mas'ud was so near to him that he was considered almost like a member of the Prophet's household. He is remembered as the first believer to recite the Qur'an publicly near the Ka'ba after the Prophet—an act of great courage in the face of the Quraysh. He memorized around seventy surahs directly from the Prophet's blessed lips and served him closely, never straying far from his side. Celebrated for his beautiful Qur'anic recitation and profound scholarship, he devoted his life to the Qur'an and Islam. He passed away in Medina in 32 AH (652 CE), at roughly 63 years old.",
    description_tr:
      "Peygamber Efendimizin (sallallâhu aleyhi ve sellem) hane halkından sayılacak kadar O'na yakın olan, İslam'ın ilk öncülerinden büyük bir âlimdir. O, Mekke'de müşriklere karşı Kâbe'de Kur'an-ı Kerim'i Allah Resûl'ünün (sallallâhu aleyhi ve sellem) ardından açıktan ilk okuyan cesur mümin olma şerefini taşır. Peygamber Efendimizin (sallallâhu aleyhi ve sellem) mübarek ağzından bizzat yetmiş sure ezberleyen ve O'nun (sallallâhu aleyhi ve sellem) bir gölgesi gibi yanından ayrılmayıp özel hizmetinde bulunan bu ilim deryası, güzel Kur'an okuyuşuyla bilinir. Ömrünü Kur'an'a ve İslam'a adayan bu gözide sahabi, Hicri 32 (Miladi 652) yılında, yaklaşık 63 yaşında Medine'de vefat etmiştir.",
    description_ar: 'عالم بالقرآن والفقه',
    era_en: '594~653 CE',
    era_tr: '594~653 MS',
    title: 'Scholar',
    title_tr: 'Alim',
    title_ar: 'العالم',
    tags: [
      'Confidant of the Prophet',
      'Ocean of Knowledge',
      "Teacher of the Qur'an",
      'Symbol of Courage',
      'Foundation of Hanafi Fiqh',
    ],
    tags_tr: [
      'Peygamberimizin Sırdaşı',
      'İlim Küpü',
      "Kur'an muallimi",
      'Cesaret Timsali',
      'Hanefi fıkhının mayası',
    ],
    tags_ar: ['العالم', 'القرآن'],
    idleVideo: buildVideoUrl('abdullah-mesud', 'abdullah_bin_mesud_idle.mp4'),
  },
  'abdullah-omer': {
    id: 'abdullah-omer',
    name: 'Hz. Abdullah b. Ömer',
    name_en: 'Abdullah ibn Umar (RA)',
    name_ar: 'عبد الله بن عمر رضي الله عنه',
    name_tr: 'Hz. Abdullah bin Ömer (RA)',
    shortName: 'Ibn Umar',
    fullName: 'Abdullah ibn Umar ibn al-Khattab',
    gender: 'male',
    image: buildImageUrl('abdullah-omer', 'abdullah_bin_omer.jpg'),
    images: buildResponsiveImageUrls('abdullah-omer', 'abdullah_bin_omer.jpg'),
    sahabeName: 'abdullah_b_o_mer_en',
    heygenAvatarId: '1188c099d1424ba7ad10d29109a171e0',
    heygenLiveAvatarId: '629b55b7-90e4-4ae8-bf5a-03fa6fd4ecfa',
    description:
      "Born in 613 CE, 'Abdullah ibn 'Umar became famous for his meticulous devotion to the Prophet's (peace and blessings be upon him) Sunnah—praying where he prayed, walking where he walked, and modeling his life on the Prophet's footsteps with extraordinary precision. He is regarded as the second most prolific narrator of hadith and a towering figure of knowledge. He avoided political conflict and consistently called for Muslim unity. For roughly 60 years he served the community through legal verdicts (fatwas), and he was known for generosity, asceticism, and deep piety. He died in Mecca in 693 CE, at over 80 years old.",
    description_tr:
      "Miladi 613 yılında dünyaya gelen Abdullah b Ömer, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) adımlarını harfiyen takip etmesiyle İslam tarihinde eşsiz bir yere sahiptir. Peygamberimizin namaz kıldığı yerde namaz kılan, yürüdüğü yoldan yürüyen İbn Ömer, sünnetin canlı bir temsilcisidir. En çok hadis rivayet eden ikinci sahabi ve devasa bir ilim abidesi olan bu mübarek zat, siyasi çekişmelerden uzak durarak daima ümmetin birliğini savunmuştur. Yaklaşık 60 yıl boyunca fetva vererek ümmete hizmet eden Abdullah b. Ömer, cömertliği, zühdü ve derin takvâsıyla tanınır. 693 yılında Mekke'de, 80 yaşını aşkın bir halde vefat etmiştir.",
    description_ar: 'ابن عمر، معروف بتقواه وعلمه',
    era_en: '614~693 CE',
    era_tr: '614~693 MS',
    title: 'Scholar',
    title_tr: 'Alim',
    title_ar: 'العالم',
    tags: ['Ascetic Scholar', 'Lover of the Sunnah', 'Man of Peace'],
    tags_tr: ['Zâhid Âlim', 'Sünnet Sevdalısı', 'Sulh ehli'],
    tags_ar: ['العالم', 'ابن عمر'],
    idleVideo: buildVideoUrl('abdullah-omer', 'abdullah_bin_omer_idle.mp4'),
  },
  'abdurrahman-avf': {
    id: 'abdurrahman-avf',
    name: 'Hz. Abdurrahman b. Avf',
    name_en: 'Abd al-Rahman ibn Awf (RA)',
    name_ar: 'عبد الرحمن بن عوف رضي الله عنه',
    name_tr: 'Hz. Abdurrahman bin Avf (RA)',
    shortName: 'Ibn Awf',
    fullName: 'Abd al-Rahman ibn Awf al-Zuhri',
    gender: 'male',
    image: buildImageUrl('abdurrahman-avf', 'abdurrahman_bin_avf.jpg'),
    images: buildResponsiveImageUrls('abdurrahman-avf', 'abdurrahman_bin_avf.jpg'),
    sahabeName: 'abdurrahman_b_avf_en',
    heygenAvatarId: '91037f757bdc44e292669d834498ee8a',
    heygenLiveAvatarId: '3f9c99fd-6457-4b94-9126-ee0fa810295c',
    description:
      "Born in 581 CE, 'Abd al-Rahman ibn 'Awf was among the earliest Muslims and one of the ten Companions promised Paradise (al-'Asharah al-Mubashsharah). After migrating to Medina, he began again from nothing, famously saying, \"Show me the way to the marketplace.\" Through honesty and remarkable skill, he built great wealth—yet became even more famous for spending it generously in God's path, earning a lasting reputation as a \"champion of giving.\" He was among the rare Companions behind whom the Messenger of God (peace and blessings be upon him) prayed during the Prophet's lifetime. Despite immense means, he lived simply and remained humble. He died in Medina in 32 AH, at around 75 years old.",
    description_tr:
      'Miladi 581 yılında doğan Abdurrahman b. Avf, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) cennetle müjdelediği meşhur on sahabiden (Aşere-i Mübeşşere) biri olup İslam\'a giren ilk sekiz kişiden biridir. Medine\'ye hicret ettiğinde "Bana çarşının yolunu gösterin" diyerek ticari dehası ve dürüstlüğüyle sıfırdan büyük bir servet edinmiş, ancak kazandığını Allah yolunda harcamaktan çekinmeyen bir "infak kahramanı" olarak tarihe geçmiştir. Peygamber Efendimizin (sallallâhu aleyhi ve sellem) hayatta iken arkasında namaz kıldığı ender şahsiyetlerden olan bu mümtaz sahabi, cömertliği ve tevazusuyla tanınır. Muazzam servetine rağmen sade bir hayat yaşayan ve malını İslam\'ın hizmetine sunan Abdurrahman b. Avf, Hicri 32 yılında, 75 yaşında Medine\'de vefat etmiştir.',
    description_ar: 'أحد العشرة المبشرين بالجنة، تاجر ثري',
    era_en: '580~652 CE',
    era_tr: '580~652 MS',
    title: 'Merchant',
    title_tr: 'Tüccar',
    title_ar: 'التاجر',
    tags: [
      'From the Ten Promised Paradise',
      'Hero of Charity',
      'Magnanimous Merchant',
      'Trustworthy Companion',
    ],
    tags_tr: ["Aşere-i Mübeşşere'den", 'İnfak kahramanı', 'Semahatli Tüccar', 'Güvenilir sahabi'],
    tags_ar: ['العشرة المبشرين', 'الثري'],
    idleVideo: buildVideoUrl('abdurrahman-avf', 'abdurrahman_bin_avf_idle.mp4'),
  },
  'bera-marur': {
    id: 'bera-marur',
    name: "Hz. Berâ İbni Ma'rûr",
    name_en: 'Al-Bara ibn Marur (RA)',
    name_ar: 'البراء بن معرور رضي الله عنه',
    name_tr: 'Hz. Bera bin Marur (RA)',
    shortName: 'Al-Bara',
    fullName: 'Al-Bara ibn Marur al-Khazraji',
    gender: 'male',
    image: buildImageUrl('bera-marur', 'bera_bin_marur.jpg'),
    images: buildResponsiveImageUrls('bera-marur', 'bera_bin_marur.jpg'),
    sahabeName: 'bera_ibni_marur_en',
    heygenAvatarId: 'c30cfceff5e948b1aa7195913c7f58a0',
    heygenLiveAvatarId: '5051ef64-48a8-4a16-ab4e-025dddf6389c',
    description:
      "A respected leader of the Khazraj, al-Bara' ibn Ma'rur is remembered as the first person to pledge allegiance to the Messenger of God (peace and blessings be upon him) at the Second Pledge of 'Aqabah. Courageous and resolute, he is especially noted for praying toward the Ka'ba even before the formal command to change the qiblah—an act seen as a sign of his deep spiritual intuition and longing for the Ka'ba. He died in 622 CE, just one month before the Prophet's migration to Medina. By his will, he was buried facing the Ka'ba—making him the first Companion to be interred that way. He is also remembered as among the first to donate a third of his wealth to the Islamic cause.",
    description_tr:
      "Hazrec kabilesinin sözü geçen liderlerinden olup İkinci Akabe Biatı'nda Peygamber Efendimizin (sallallâhu aleyhi ve sellem) elini tutarak O'na ilk biat eden sahabidir. Cesareti ve kararlılığıyla tanınan Berâ'nın en bariz özelliği, henüz emir gelmediği halde gönlüne doğan ilhamla namazlarını Kâbe'ye dönerek kılan ilk kişi olmasıdır. Bu davranışı, onun engin ferasetini ve Kâbe'ye olan derin özlemini gösteren en önemli alâmet-i farikasıdır. Peygamber Efendimizin (sallallâhu aleyhi ve sellem) hicretinden sadece bir ay önce, 622 yılında vefat etmiştir. Vasiyeti üzerine kabrinde yüzü Kâbe'ye çevrilerek defnedilen ilk sahabi olma şerefine erişmiştir. Ayrıca malının üçte birini İslam davası için bağışlayan ilk kişi olarak da bilinen Berâ, sadakati ve bu öncü duruşuyla İslam tarihinde eşsiz bir iz bırakmıştır.",
    description_ar: 'قائد الأنصار، أول من بايع في العقبة',
    era_en: '560~622 CE',
    era_tr: '560~622 MS',
    title: 'Leader of Ansar',
    title_tr: 'Ensar Lideri',
    title_ar: 'قائد الأنصار',
    tags: ['Man of Firsts', 'From the Inspired Ones', 'Courageous Leader'],
    tags_tr: ['İlklerin Adamı', "Mülhemûn'dan", 'Cesur Lider'],
    tags_ar: ['الأنصار', 'العقبة'],
    idleVideo: buildVideoUrl('bera-marur', 'bera_bin_marur_idle.mp4'),
  },
  bilal: {
    id: 'bilal',
    name: 'Hz. Bilâl-i Habeşî',
    name_en: 'Bilal ibn Rabah (RA)',
    name_ar: 'بلال بن رباح رضي الله عنه',
    name_tr: 'Hz. Bilal-i Habeşi (RA)',
    shortName: 'Bilal',
    fullName: 'Bilal ibn Rabah al-Habashi',
    gender: 'male',
    image: buildImageUrl('bilal', 'bilal_habesi.jpg'),
    images: buildResponsiveImageUrls('bilal', 'bilal_habesi.jpg'),
    video: buildVideoUrl('bilal', 'bilal_intro.mp4'),
    sahabeName: 'bilal_habes_i_en',
    heygenAvatarId: '38f6357bee804fe0a69914e57e04cc50',
    heygenLiveAvatarId: '308caba5-e035-42ad-87ac-34d1b3f26c10',
    description:
      'Born in 581 CE, Bilal al-Habashi is one of the most revered figures in early Islamic history and is honored as Islam\'s first muezzin (caller to Prayer). Originally an enslaved man of Abyssinian (Ethiopian) origin, he embraced Islam and became a symbol of unwavering faith—famously crying out "Ahad, Ahad" (God is The Unique) even while being tortured on the scorching sands of Mecca. Known for his powerful, moving voice, Bilal remained a loyal Companion of the Prophet Muhammad (peace and blessings be upon him) in both peace and travel. Through patience and surrender to God, he rose from bondage to the rank of "the master of the muezzins." He passed away in Damascus in 641 CE, reportedly in his early 60s.',
    description_tr:
      'Miladi 581 yılında dünyaya gelen Bilâl-i Habeşî, İslam tarihinin en gözide simalarından biri olup "İslâm\'ın ilk müezzini" şerefine erişmiş büyük bir sahabidir. O, Habeşistan kökenli bir köle iken Müslüman olmuş, Mekke\'nin kızgın kumlarında ağır işkenceler görürken dahi "Ehad, Ehad" (Allah birdir) diyerek haykırdığı sarsılmaz imanı ve dik duruşuyla sembolleşmiştir. Gür ve yanık sesiyle okuduğu ezanlarla özdeşleşen Hz. Bilâl, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) yanından hazarda ve seferde hiç ayrılmamış sadık bir dosttur. Sabrı ve teslimiyetiyle kölelikten "müezzinlerin efendisi" unvanına yükselen bu mübarek zat, 641 yılında Şam\'da 60 yaşlarında vefat etmiştir.',
    description_ar:
      'من أعظم الشخصيات في التاريخ الإسلامي المبكر، شرفه الله بأن يكون أول مؤذن في الإسلام. كان حبشي الأصل، حاول أعداؤه إذلاله لكنه ظل ثابتاً على إيمانه.',
    era_en: '581~641 CE',
    era_tr: '581~641 MS',
    title: 'First Muezzin of Islam',
    title_tr: 'İlk Müezzin',
    title_ar: 'المؤذن الأول',
    tags: ['Master of Muezzins', 'Hero of Patience', 'Loyal Treasurer'],
    tags_tr: ['Müezzinlerin efendisi', 'Sabır kahramanı', 'Sadık Hazinedar'],
    tags_ar: ['المؤذن', 'الصحابي', 'المخلص'],
    idleVideo: buildVideoUrl('bilal', 'bilal_idle.mp4'),
  },
  'ebu-hureyre': {
    id: 'ebu-hureyre',
    name: 'Hz. Ebû Hureyre',
    name_en: 'Abu Hurayra (RA)',
    name_ar: 'أبو هريرة رضي الله عنه',
    name_tr: 'Hz. Ebu Hureyre (RA)',
    shortName: 'Abu Hurayrah',
    fullName: 'Abd al-Rahman ibn Sakhr al-Dawsi (Abu Hurayrah)',
    gender: 'male',
    image: buildImageUrl('ebu-hureyre', 'ebu_hureyre.jpg'),
    images: buildResponsiveImageUrls('ebu-hureyre', 'ebu_hureyre.jpg'),
    sahabeName: 'ebu_hureyre_en',
    heygenAvatarId: 'babbeece228242c4a72d4665042ee43b',
    heygenLiveAvatarId: '48cbfc22-14be-436e-9738-ec8a205e2123',
    description:
      'Abu Hurayrah is the Companion most famous for transmitting the Prophet\'s hadiths to the Ummah (the global Muslim community). Renowned for his exceptional memory and his intense thirst for knowledge, he was born in Yemen around the early 600s CE. After accepting Islam, he turned away from worldly comforts and devoted himself to learning—staying close to the Prophet (peace and blessings be upon him) and spending his days in the Prophet\'s Mosque among the Ashab as-Suffah (the People of the Bench). His nickname, meaning "Father of the Kitten," reflects his gentleness and compassion toward a cat. He received special attention and prayers from the Prophet and became one of the most important pillars of hadith transmission. He died in Medina in 58 AH, at around 78 years of age.',
    description_tr:
      "Peygamber Efendimizin (sallallâhu aleyhi ve sellem) hadislerini İslam ümmetine en çok aktaran, güçlü hafızası ve ilme olan büyük iştiyakıyla tanınan müstesna bir sahabidir. Yemen'de miladi yaklaşık 600'lü yıllarda doğmuş ve gelip Müslüman olduktan sonra dünya nimetlerine iltifat etmemiş, hayatını Peygamber Efendimizin (sallallâhu aleyhi ve sellem) yanından hiç ayrılmadan Mescid-i Nebevî'deki Suffa'da ilim tahsiline adamıştır. Bir kediye gösterdiği merhamet sebebiyle \"Kedicik Babası\" manasındaki meşhur künyesiyle anılan bu mütevazı zat, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) özel iltifatına ve duasına mazhar olmuştur. Hadis ilminin en önemli taşıyıcısı olan Ebû Hureyre, Hicri 58 yılında 78 yaşındayken Medine'de vefat etmiştir.",
    description_ar:
      'الصحابي الذي روى أحاديث النبي أكثر من أي صحابي آخر، معروف بذاكرته الاستثنائية وحرصه على العلم. لُقب بـ"أبو هريرة" لرحمته بالهرة.',
    era_en: '603~681 CE',
    era_tr: '603~681 MS',
    title: 'Hadith Narrator',
    title_tr: 'Hadis Ravisi',
    title_ar: 'راوي الأحاديث',
    tags: [
      'Narrator of the Most Hadiths',
      'Imam of Suffa',
      'Lover of Knowledge',
      'Ascetic and Humble Companion',
      'Animal Lover',
    ],
    tags_tr: [
      'En çok hadis rivayet eden sahabi',
      "Suffe'nin imamı",
      'İlim Âşığı',
      'Zahid ve mütevazı sahabi',
      'Hayvansever sahabi',
    ],
    tags_ar: ['الراوي', 'الصحابي'],
    idleVideo: buildVideoUrl('ebu-hureyre', 'ebu_hureyre_idle.mp4'),
  },

  'ebu-sufyan': {
    id: 'ebu-sufyan',
    name: 'Hz. Ebû Süfyân',
    name_en: 'Abu Sufyan (RA)',
    name_ar: 'أبو سفيان رضي الله عنه',
    name_tr: 'Hz. Ebu Süfyan (RA)',
    shortName: 'Abu Sufyan',
    fullName: 'Sakhr ibn Harb (Abu Sufyan)',
    gender: 'male',
    image: buildImageUrl('ebu-sufyan', 'ebu_sufyan.jpg'),
    images: buildResponsiveImageUrls('ebu-sufyan', 'ebu_sufyan.jpg'),
    sahabeName: 'ebu_su_fyan_en',
    heygenAvatarId: '8550d92ff1cd43d796d73ace822ed8ca',
    heygenLiveAvatarId: '84f0ea53-3284-469c-8be4-b4b9b1373a69',
    description:
      'Born around 560 CE, Abu Sufyan was a prominent Quraysh leader and wealthy merchant, among the rare literate figures of his time in Mecca. After years of opposition, he accepted Islam on the day of the Conquest of Mecca. That day, the Messenger of God (peace and blessings be upon him) declared: "Whoever enters the house of Abu Sufyan is safe," a statement that became historically famous. Through his daughter Umm Habibah, he was also the Prophet\'s father-in-law. After embracing Islam, he devoted himself to making amends through service, even joining campaigns in old age. Reports mention that he lost his eyesight while striving in God\'s path. He is said to have died in Medina at the age of 88.',
    description_tr:
      "Miladi 560 civarında doğan, Kureyş'in sözü dinlenen reisi, zengin bir tâccar ve o dönem Mekke'sinin okuma yazma bilen ender isimlerindendir. Uzun yıllar süren direnişin ardından Mekke'nin Fethi günü İslam'la şereflenmiş, o gün Peygamber Efendimizin \"Kim Ebû Süfyân'ın evine girerse emniyettedir\" iltifatına mazhar olarak tarihe geçmiştir. Aynı zamanda Ümmü Habîbe validemiz vesilesiyle Peygamber Efendimizin kayınpederi olma onuruna erişmiştir. İman ettikten sonra ömrünü İslam'a hizmete adayan bu sahabi, geçmiş günleri telafi edercesine ileri yaşına rağmen cephelere koşmuştur. Taif ve Yermûk savaşlarında gösterdiği büyük fedakarlıklarla, İslam ordusunu cesaretlendirirken iki gözünü de Allah yolunda feda eden Ebû Süfyân, 88 yaşında Medine'de vefat etmiştir.",
    description_ar: 'قائد قريش السابق الذي اعتنق الإسلام',
    era_en: '560~650 CE',
    era_tr: '560~650 MS',
    title: 'Leader of Quraysh',
    title_tr: 'Kureyş Lideri',
    title_ar: 'قائد قريش',
    tags: ['Moderate Leader', 'Selfless Companion'],
    tags_tr: ['Mutedil Lider', 'Fedakâr sahabi'],
    tags_ar: ['القائد', 'مكة'],
    idleVideo: buildVideoUrl('ebu-sufyan', 'ebu_sufyan_idle.mp4'),
  },
  'ebu-zerr': {
    id: 'ebu-zerr',
    name: 'Hz. Ebû Zer el-Gıfârî',
    name_en: 'Abu Dharr al-Ghifari (RA)',
    name_ar: 'أبو ذر الغفاري رضي الله عنه',
    name_tr: 'Hz. Ebu Zer el-Gıfari (RA)',
    shortName: 'Abu Dharr',
    fullName: 'Jundub ibn Junadah al-Ghifari (Abu Dharr)',
    gender: 'male',
    image: buildImageUrl('ebu-zerr', 'ebu_zer.jpg'),
    images: buildResponsiveImageUrls('ebu-zerr', 'ebu_zer.jpg'),
    sahabeName: 'ebu_zerr_el-gifari_en',
    heygenAvatarId: 'a9d958c561dd470ebc87734706afc14f',
    heygenLiveAvatarId: '3688944a-f0ea-4fb6-8d73-3aac1026cf7e',
    description:
      'Although his exact birth date is unknown, Abu Dharr was among the earliest to embrace Islam and is remembered for his courage and uncompromising truthfulness. The Messenger of God (peace and blessings be upon him) is reported to have said of him: "Under the sky and upon the earth there is no one more truthful than him." Detached from worldly wealth and accumulation, he became known for his asceticism and piety—so much so that he was sometimes called "the Messiah of Islam." He is also remembered as a defender of the poor and the oppressed. In line with a Prophetic statement about him, he lived, walked, and died in striking solitude—a mark of his steadfast loyalty. He died in al-Rabadhah, a desert region, in 32 AH (653 CE).',
    description_tr:
      'Doğum tarihi kesin bilinmemekle birlikte İslam\'la şereflenen ilklerden olan bu cesur sahabi, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) "Gök kubbenin altında ve yeryüzünün üstünde ondan daha doğru sözlü kimse yoktur" iltifatına mazhar olmuştur. Dünya malına ve servet biriktirmeye önem vermeyen, zühdü ve takvasıyla "İslam\'ın Mesihi" olarak anılan Ebû Zer, açık sözlülüğü ve fakirlerin hakkını savunmasıyla tanınır. Ebû Zer, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) Tebük Seferi sırasında müjdelediği üzere "yalnız yürüyen, yalnız yaşayan ve yalnız ölen" bir sadakat timsalidir. Hayatını sade bir şekilde sürdüren bu büyük sahabi, Hicri 32 (M. 653) yılında Rebeze çölünde vefat etmiştir.',
    description_ar:
      'من أوائل المسلمين، معروف بشجاعته وصدقه الذي لا يخاف في الحق لومة لائم. قال الرسول: "ما أظلت الخضراء ولا أقلت الغبراء أصدق لساناً من أبي ذر"',
    era_en: '568~652 CE',
    era_tr: '568~652 MS',
    title: 'Truthful Ascetic',
    title_tr: 'Zahid',
    title_ar: 'الزاهد الصادق',
    tags: ['Ascetic and Pious Companion', 'The Lonely Man', 'Symbol of Loyalty'],
    tags_tr: ['Zahid ve müttaki sahabi', 'Yalnız adam', 'Sadakat timsali'],
    tags_ar: ['الزاهد', 'الصادق', 'الصحابي'],
    idleVideo: buildVideoUrl('ebu-zerr', 'ebu_zer_idle.mp4'),
  },
  'ebul-heysem': {
    id: 'ebul-heysem',
    name: "Hz. Ebu'l-Heysem Mâlik b. Teyyihân",
    name_en: 'Abul-Haysam ibn al-Tayhan (RA)',
    name_ar: 'أبو الهيثم بن التيهان رضي الله عنه',
    name_tr: 'Hz. Ebul-Heysem bin Teyyihan (RA)',
    shortName: 'Abu al-Haytham',
    fullName: 'Malik ibn al-Tayyihan (Abu al-Haytham)',
    gender: 'male',
    image: buildImageUrl('ebul-heysem', 'ebul_heysem_malik_bin_teyyihan.jpg'),
    images: buildResponsiveImageUrls('ebul-heysem', 'ebul_heysem_malik_bin_teyyihan.jpg'),
    sahabeName: 'ebul_heysem_malik_b_teyyihan_en',
    heygenAvatarId: 'd1fb240dc73d4cbbaddc6a7d7b351c22',
    heygenLiveAvatarId: 'f6939a68-537c-4979-9879-c20fbe67c7a7',
    description:
      'Even in the pre-Islamic period, Abu al-Haytham is said to have turned away from idol worship. He was among the first Medinans to pledge allegiance at \'Aqabah and was chosen as one of the twelve representatives (naqibs). Celebrated for exceptional bravery, he was nicknamed Dhu al-Sayfayn—"the Man of Two Swords." He is also remembered as the host of a well-known "blessed meal" in which the Messenger of God (peace and blessings be upon him), Abu Bakr, and \'Umar visited his home and were served fresh dates and meat. He died in Medina around 20 AH (641 CE), according to many reports.',
    description_tr:
      "Cahiliye döneminde dahi putlardan yüz çeviren bu gözide sahabi, Akabe biatlarında Peygamber Efendimizin (sallallâhu aleyhi ve sellem) elini tutan ilk Medinelilerden ve O'nun seçtiği on iki temsilciden (nakîb) biridir. İslam uğruna girdiği savaşlarda gösterdiği üstün cesaretle, aynı anda iki kılıç kullanan manasına gelen \"Zü's-seyfeyn\" (İki Kılıç Sahibi) lakabıyla tarihe geçmiştir. Ebu'l-Heysem, bir gün Peygamber Efendimizin (sallallâhu aleyhi ve sellem), Hz. Ebû Bekir ve Hz. Ömer ile birlikte evine teşrif ettiği ve onlara taze hurma ile et ikram ettiği o meşhur \"bereketli ziyafet\" hadisesinin ev sahibidir. Ömrünü İslam'a adayan bu cömert sahabi, kaynakların çoğunluğuna göre Hicri 20 (M. 641) yılında Medine'de vefat etmiştir.",
    description_ar: 'صاحب السيفين',
    era_en: '580~640 CE',
    era_tr: '580~640 MS',
    title: 'Warrior',
    title_tr: 'Savaşçı',
    title_ar: 'المحارب',
    tags: ['Valiant Companion', 'Matchless Host', 'Pioneer Hanif'],
    tags_tr: ['Yiğit sahabi', 'Eşsiz Misafirperver', 'Öncü Hanîf'],
    tags_ar: ['الأنصار', 'المحارب'],
    idleVideo: buildVideoUrl('ebul-heysem', 'ebul_heysem_malik_bin_teyyihan_idle.mp4'),
  },
  'esma-bint-ebi-bekr': {
    id: 'esma-bint-ebi-bekr',
    name: 'Hz. Esma bint Ebû Bekir',
    name_en: 'Asma bint Abu Bakr (RA)',
    name_ar: 'أسماء بنت أبي بكر رضي الله عنها',
    name_tr: 'Hz. Esma bint Ebu Bekir (RA)',
    shortName: 'Asma',
    fullName: 'Asma bint Abu Bakr al-Siddiq',
    gender: 'female',
    image: buildImageUrl('esma-bint-ebi-bekr', 'esma_bint_ebu_bekir.jpg'),
    images: buildResponsiveImageUrls('esma-bint-ebi-bekr', 'esma_bint_ebu_bekir.jpg'),
    sahabeName: 'esma_bint_ebi_bekr_en',
    heygenAvatarId: 'a01562160bf44b6ba15f3c4f3387a293',
    heygenLiveAvatarId: 'b81175ed-c593-463c-9694-748c3b255db4',
    description:
      'The daughter of Abu Bakr, Asma\' was among the earliest Muslims. She is especially remembered for her courage during the Hijrah, when she secretly carried food to the Messenger of God (peace and blessings be upon him) and her father while they were in hiding. She tore her waistband into two to secure the provisions, and for this she was honored with the title Dhat al-Nitaqayn—"the Woman of the Two Belts." Known for generosity, strong resolve, and a remarkable memory, she also became a symbol of patience and steadfastness, living nearly a 100 years. She is often mentioned as among the last of the early emigrants to pass away, and she died in Mecca in 692 CE.',
    description_tr:
      "Hz. Ebû Bekir'in kızı olan Hz. Esma, İslam'ı ilk kabul eden öncülerdendir. Onun en belirgin alamet-i farikası, zorlu hicret yolculuğunda Peygamber Efendimizin (sallallâhu aleyhi ve sellem) ve babasına gizlice yemek taşırken gösterdiği fedakârlıktır. Azık torbasını bağlamak için belindeki kuşağını ikiye böldüğü için bizzat Peygamber Efendimiz (sallallâhu aleyhi ve sellem) tarafından cennetle müjdelenmiş ve \"Zâtü'n-nitâkayn\" (İki Kuşak Sahibi) lakabıyla onurlandırılmıştır. Cömertliği, sarsılmaz cesareti ve güçlü hafızasıyla tanınan Hz. Esma, aynı zamanda sabır timsali bir annedir. Yaklaşık 100 yaşına ulaşan bereketli ömrü boyunca, karşılaştığı zorluklara karşı dik duruşuyla tüm Müslümanlar için örnek bir şahsiyet olmuştur. Muhacirlerin son vefat edeni olarak bilinen bu gözide hanımefendi, 692 yılında Mekke'de vefat etmiştir.",
    description_ar: 'ذات النطاقين',
    era_en: '595~692 CE',
    era_tr: '595~692 MS',
    title: 'Dhat an-Nitaqayn',
    title_tr: "Zatü'n-Nitakayn",
    title_ar: 'ذات النطاقين',
    tags: [
      'Valiant and Courageous Female Companion',
      'From the Long-Lived',
      'Matchlessly Generous',
      'Selfless Female Companion',
      'Tremendous Willpower',
    ],
    tags_tr: [
      'Yiğit ve cesur kadın sahabi',
      "Muammerûn'dan",
      'Eşsiz Cömert',
      'Fedakâr kadın sahabi',
      'Müthiş irade',
    ],
    tags_ar: ['ابنة أبي بكر'],
    idleVideo: buildVideoUrl('esma-bint-ebi-bekr', 'esma_bint_ebu_bekir_idle.mp4'),
  },
  hind: {
    id: 'hind',
    name: 'Hz. Hind bint Utbe',
    name_en: 'Hind bint Utba (RA)',
    name_ar: 'هند بنت عتبة رضي الله عنها',
    name_tr: 'Hz. Hind bint Utbe (RA)',
    shortName: 'Hind',
    fullName: 'Hind bint Utba ibn Rabia',
    gender: 'female',
    image: buildImageUrl('hind', 'hind_bint_utbe.jpg'),
    images: buildResponsiveImageUrls('hind', 'hind_bint_utbe.jpg'),
    sahabeName: 'hind_bint_utbe_en',
    heygenAvatarId: '8c453c49c8834b7dae154ed6c55905a4',
    heygenLiveAvatarId: '8d1a1878-0517-4236-a721-25d808803201',
    description:
      "From a noble Meccan family and known for sharp speech, eloquence, and strong intelligence, Hind was among the most outspoken opponents of Islam in the pre-Islamic period. Her name became associated with the bitterness of Uhud, yet after the conquest of Mecca she received the Prophet's forgiveness and embraced Islam. After accepting faith, she redirected her fierce temperament into devotion and service, even joining the campaign of Yarmuk to uplift the army's morale despite her age. She was also the mother of Mu'awiyah. Reports place her death during the caliphate of 'Umar, around the age of 55—an emblem of a striking journey from darkness to light.",
    description_tr:
      "Mekke'nin soylu ailesine mensup, sözünü sakınmayan güçlü karakteri, fesahati ve keskin zekâsı ile tanınan Hind; Câhiliye döneminde İslam'a karşı en sert duruşu sergileyen kadınların başında gelirdi. Uhud'daki intikam hırsıyla hafızalara kazınan bu mağrur kadın, Mekke'nin fethinden sonra Peygamber Efendimizin (sallallâhu aleyhi ve sellem) affına mazhar olarak hidayeti bulmuştur. İman ettikten sonra o eski hırsını İslam'a hizmet aşkına dönüştüren Hind, ilerleyen yaşına rağmen Yermûk'e katılarak ordunun maneviyatını yükseltmiştir. Emevî Devleti'nin kurucusu Hz. Muâviye'nin de annesi olan bu dirayetli sahabi, Hz. Ömer devrinde yaklaşık 55 yaşlarında vefat etmiştir. O, karanlıktan aydınlığa uzanan ibretlik bir dönüşümün simgesidir.",
    description_ar: 'امرأة قوية اعتنقت الإسلام بعد فتح مكة',
    era_en: '580~655 CE',
    era_tr: '580~655 MS',
    title: 'Noblewoman',
    title_tr: 'Soylu Kadın',
    title_ar: 'السيدة النبيلة',
    tags: [
      'Courageous Female Companion',
      'Possessor of Sharp Intellect',
      'Late-Blooming Virtue',
      'Symbol of Return from Darkness to Light',
    ],
    tags_tr: [
      'Cesur kadın sahabi',
      'Keskin Zekâ sahibi',
      'Geç de olsa mecraını bulan nitelik',
      'Karanlıktan aydınlığa dönüşün simgesi',
    ],
    tags_ar: ['قريش', 'الصحابيات'],
    idleVideo: buildVideoUrl('hind', 'hind_bint_utbe_idle.mp4'),
  },
  'kab-malik': {
    id: 'kab-malik',
    name: "Hz. Ka'b b. Mâlik",
    name_en: "Ka'b ibn Malik (RA)",
    name_ar: 'كعب بن مالك رضي الله عنه',
    name_tr: "Hz. Ka'b bin Malik (RA)",
    shortName: "Ka'b",
    fullName: "Ka'b ibn Malik al-Ansari",
    gender: 'male',
    image: buildImageUrl('kab-malik', 'kab_bin_malik.jpg'),
    images: buildResponsiveImageUrls('kab-malik', 'kab_bin_malik.jpg'),
    sahabeName: 'kab_b_malik_en',
    heygenAvatarId: '23f5495708744115a099056d42b7b4c8',
    heygenLiveAvatarId: 'f936bb5b-4cd1-44e8-8dd4-b909323c5701',
    description:
      'Born in Medina roughly 27 years before the Hijrah, Ka\'b ibn Malik was a leading figure among the Ansar and one of the Prophet\'s three famous poets. He defended Islam not only on the battlefield but also with his eloquent, forceful poetry—earning the title "the Prophet\'s poet." His defining hallmark, however, was his uncompromising honesty. When he failed to join the Tabuk expedition, he refused to invent excuses and told the truth; his repentance is famously linked to Qur\'anic verses announcing forgiveness—making him a "hero of repentance." He died in Medina in 50 AH, after a blessed life of about 77 years.',
    description_tr:
      "Hicretten yaklaşık 27 yıl önce Medine'de doğan Ka'b, Ensar'ın ileri gelenlerinden ve Peygamber Efendimizin (sallallâhu aleyhi ve sellem) üç büyük şairinden biridir. O, İslam'ı hem kılıcıyla hem de düşmanlara karşı okuduğu etkili şiirleriyle savunarak \"Peygamber Şairi\" unvanını kazanmıştır. Onun asıl alamet-i farikası ise eşsiz doğruluğudur. Tebük Seferi'ne katılamadığı halde bahane uydurmayıp dürüst davrandığı için hakkında inen ayetlerle affedilmiş bir \"tevbe kahramanı\"dır. Sadakatiyle tanınan bu yüce sahabi, 77 yıllık bereketli ömrünün ardından Hicri 50 senesinde Medine'de vefat etmiştir.",
    description_ar: 'شاعر النبي',
    era_en: '594~670 CE',
    era_tr: '594~670 MS',
    title: 'Poet',
    title_tr: 'Şair',
    title_ar: 'الشاعر',
    tags: ['Poet of the Prophet', 'Hero of Truthfulness', 'Hero of Patience', 'Valiant Companion'],
    tags_tr: ['Peygamber Şairi', 'Doğruluk kahramanı', 'Sabır kahramanı', 'Yiğit sahabi'],
    tags_ar: ['الشاعر', 'الأنصار'],
    idleVideo: buildVideoUrl('kab-malik', 'kab_bin_malik_idle.mp4'),
  },
  musab: {
    id: 'musab',
    name: "Hz. Mus'ab b. Umeyr",
    name_en: "Mus'ab ibn Umayr (RA)",
    name_ar: 'مصعب بن عمير رضي الله عنه',
    name_tr: "Hz. Mus'ab bin Umeyr (RA)",
    shortName: "Mus'ab",
    fullName: "Mus'ab ibn Umayr al-Qurashi",
    gender: 'male',
    image: buildImageUrl('musab', 'musab_bin_umeyr.jpg'),
    images: buildResponsiveImageUrls('musab', 'musab_bin_umeyr.jpg'),
    sahabeName: 'musab_bin_u_meyr_en',
    heygenAvatarId: '9534c72e842a40e39f55b73b508a06aa',
    heygenLiveAvatarId: '2e64c61e-09ac-46e4-9786-1a941f9125b1',
    description:
      "Raised in luxury in Mecca, Mus'ab chose Islam and gave up everything worldly for his faith. The Prophet Muhammad (peace and blessings be upon him) appointed him as the first teacher (mu'allim) sent to Medina. After the pledges of 'Aqabah, Mus'ab went to the city, teaching the oneness of God and establishing prescribed Prayer—helping transform Medina spiritually and preparing it for the Hijrah. His sacrifices and success in conveying the message made him central to Islam's early spread. He served as the standard-bearer at Uhud and was martyred there at about 40 years of age.",
    description_tr:
      "Mekke'nin refahı içinde büyüyüp İslam'ı seçince dünyalık her şeyden vazgeçen, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) \"ilk muallim\" olarak Medine'ye görevlendirdiği asil bir sahabidir. Akabe görüşmelerinin ardından Medine'ye giderek insanlara hakikati, kelime-i tevhidi ve namazı anlatmış, şehri manevi bir havaya büründürerek hicrete hazırlamıştır. İnancı uğruna büyük fedakarlıklar gösteren Mus'ab, tebliğ görevini büyük bir başarıyla ifa ederek İslam'ın yayılmasında ve kalplerin ısınmasında kilit rol oynamıştır. Uhud'da İslam ordusunun sancaktarı olarak şehit olduğunda 40 yaşındaydı.",
    description_ar: 'الصحابي الأنيق الذي ترك ثروته من أجل الإسلام',
    era_en: '594~625 CE',
    era_tr: '594~625 MS',
    title: 'First Ambassador',
    title_tr: 'İlk Elçi',
    title_ar: 'السفير الأول',
    tags: ['Teacher Companion', 'Selfless Companion', 'Standard-Bearer'],
    tags_tr: ['Muallim sahabi', 'Fedakâr sahabi', 'Sancaktar'],
    tags_ar: ['السفير', 'أحد'],
    idleVideo: buildVideoUrl('musab', 'musab_bin_umeyr_idle.mp4'),
  },
  'nesibe-bint-kab': {
    id: 'nesibe-bint-kab',
    name: "Hz. Ümmü Ümâre, Nesibe bint Kâ'b",
    name_en: 'Nusaybah bint Kaab (RA)',
    name_ar: 'نسيبة بنت كعب رضي الله عنها',
    name_tr: 'Hz. Nesibe bint Kab (Ümmü Umare) (RA)',
    shortName: 'Nusaybah',
    fullName: 'Nusaybah bint Kaab al-Ansariyyah',
    gender: 'female',
    image: buildImageUrl('nesibe-bint-kab', 'nesibe_bint_kab.jpg'),
    images: buildResponsiveImageUrls('nesibe-bint-kab', 'nesibe_bint_kab.jpg'),
    sahabeName: 'nesibe_u_mmu_u_mare_en',
    heygenAvatarId: '8822c98c0afc4ed09ba3dc5801905b23',
    heygenLiveAvatarId: '97aa7250-e8a6-42ba-90a0-a63cb5ae8e03',
    description:
      "One of the first women of Medina to be honored with Islam, Umm 'Umarah was a Companion of unshakeable faith and extraordinary courage. She was one of the two women who took part in the Pledge of 'Aqabah, but her greatest legend was written at Uhud. At the most critical moments, she stood around the Messenger of God (peace and blessings be upon him) like a living shield—absorbing severe wounds yet never stepping back from protecting him. The Prophet praised her, saying, \"Wherever I turned, I saw her fighting.\" She is also remembered as a devoted mother and as someone who granted the glad tidings of being the Prophet's neighbor in Paradise. She passed away in 13 AH (634 CE).",
    description_tr:
      "Medine'nin İslâm ile şereflenen ilk kadınlarından olan bu cesur sahabi, sarsılmaz bir imana ve üstün bir cesarete sahiptir. Akabe Biatı'na katılan iki kadından biri olan Ümmü Ümâre, asıl destanını Uhud'da yazmıştır. Savaşın en kritik anlarında Peygamber Efendimizin (sallallâhu aleyhi ve sellem) etrafında adeta etten bir kalkan olmuş, aldığı ağır yaralara rağmen O'nu korumaktan vazgeçmemiştir. Peygamber Efendimizin (sallallâhu aleyhi ve sellem), \"Nereye baksam onun çarpıştığını görüyordum\" övgüsüne mazhar olan bu fedakâr anne, cennette de O'na komşu olma şerefine erişmiştir. Hem savaş meydanındaki kahramanlığı hem de Kur'an ayetlerinin nüzulüne vesile olan ferasetiyle tanınan Nesibe validemiz, hicretin 13. yılında (M. 634) Hakk'ın rahmetine kavuşmuştur.",
    description_ar: 'المرأة المحاربة التي دافعت عن النبي في أحد',
    era_en: '590~634 CE',
    era_tr: '590~634 MS',
    title: 'Warrior',
    title_tr: 'Savaşçı',
    title_ar: 'المحاربة',
    tags: ['Courageous Female Companion', 'Selfless Mother', 'From the Pioneer Women'],
    tags_tr: ['Cesur kadın sahabi', 'Fedakâr anne', 'Öncü kadınlardan'],
    tags_ar: ['الصحابيات', 'أحد'],
    idleVideo: buildVideoUrl('nesibe-bint-kab', 'nesibe_bint_kab_idle.mp4'),
  },
  'ummu-eymen': {
    id: 'ummu-eymen',
    name: 'Hz. Ümmü Eymen',
    name_en: 'Umm Ayman (RA)',
    name_ar: 'أم أيمن رضي الله عنها',
    name_tr: 'Hz. Ümmü Eymen (RA)',
    shortName: 'Umm Ayman',
    fullName: 'Barakah bint Thalaba (Umm Ayman)',
    gender: 'female',
    image: buildImageUrl('ummu-eymen', 'ummu_eymen.jpg'),
    images: buildResponsiveImageUrls('ummu-eymen', 'ummu_eymen.jpg'),
    sahabeName: 'u_mmu_eymen_en',
    heygenAvatarId: '4e7df61c3add41cbbb2884e4d737f241',
    heygenLiveAvatarId: '799aea2d-d386-480a-8972-b7d80192852a',
    description:
      'Umm Ayman was the devoted nursemaid who held the Prophet Muhammad (peace and blessings be upon him) in her arms the moment he was born, helped raise him, and earned his heartfelt praise: "You are my mother after my mother." She was the most precious legacy left to him by his mother Amina, a woman of Abyssinian (Ethiopian) origin whom the Prophet honored as part of his own family and gave glad tidings of Paradise. This loyal Companion witnessed both the Prophet\'s birth and his passing, embodying unwavering faithfulness. She lived a blessed and long life, passing away to her Lord in the early years of Caliph Uthman\'s rule (death: 644 CE).',
    description_tr:
      'Ümmü Eymen, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) dünyaya gözlerini açtığı an kucağına aldığı, O\'nu büyüten ve "Annemden sonra annem" iltifatına mazhar olan fedakâr dadısıdır. Annesi Amine\'den kendisine kalan en değerli emanet olup, Efendimizin "Ailemden geriye kalan" diyerek taltif ettiği, Habeş asıllı ve cennetle müjdelenmiş bir hanımdır. Peygamberimizin hem doğumuna hem de vefatına şahitlik eden bu vefalı sahabi, Hz. Osman\'ın halifeliğinin ilk yılında, hayırlı ve uzun bir ömür sürdükten sonra (Vefat: M.S. 644) Hakk\'a yürümüştür.',
    description_ar: 'الأم بعد الأم',
    era_en: '550~644 CE',
    era_tr: '550~644 MS',
    title: 'Caretaker',
    title_tr: 'Bakıcı',
    title_ar: 'الحاضنة',
    tags: ['Loyal Nanny', 'People of Paradise', 'Wise', 'Witness of Prophetic Life'],
    tags_tr: ['Vefalı dadı', 'Cennet ehli', 'Hikmetli', 'Nebevi Hayatın şahidi'],
    tags_ar: ['الحاضنة', 'العائلة'],
    idleVideo: buildVideoUrl('ummu-eymen', 'ummu_eymen_idle.mp4'),
  },
  'enes-malik': {
    id: 'enes-malik',
    name: 'Hz. Enes b. Mâlik',
    name_en: 'Anas ibn Malik (RA)',
    name_ar: 'أنس بن مالك رضي الله عنه',
    name_tr: 'Hz. Enes b. Mâlik (RA)',
    shortName: 'Anas',
    fullName: 'Anas ibn Malik al-Ansari',
    gender: 'male',
    image: buildImageUrl('enes-malik', 'enes_bin_malik.jpg'),
    images: buildResponsiveImageUrls('enes-malik', 'enes_bin_malik.jpg'),
    sahabeName: 'enes_b_malik_en',
    heygenAvatarId: '39e0bec73bcf4395af00c6ab31e86389',
    heygenLiveAvatarId: '76beb246-c26f-48b0-bf07-14c83a755b9d',
    description:
      'Born around 612 CE, Anas is known as Khadim al-Nabi—the Prophet\'s personal attendant. At about ten years old, his mother brought him to serve the Prophet Muhammad (peace and blessings be upon him), and he remained with him for ten years—learning the Prophet\'s character directly through daily life. The Prophet prayed for him: "O God, increase his wealth and children, and bless him with a long life." Anas became one of the seven most prolific hadith narrators and played a crucial role in preserving the Sunnah for later generations. Known for his careful worship and resemblance to the Prophet in manners, he lived a long life and died in Basra in 93 AH (711–712 CE), at about 103 years old—often remembered as the last Companion to pass away.',
    description_tr:
      "612 yılı civarında doğmuş olup İslam tarihinde \"Hâdimü'n-Nebî\" yani Peygamber Efendimizin (sallallâhu aleyhi ve sellem) özel hizmetkârı unvanıyla tanınan müstesna bir sahabidir. Henüz on yaşındayken annesi tarafından Peygamber Efendimizin (sallallâhu aleyhi ve sellem) hizmetine verilen Enes, on yıl boyunca O'nun yanından hiç ayrılmamış, terbiyesiyle yetişmiş ve O'nun ahlakını bizzat yaşayarak öğrenmiştir. Peygamber Efendimizin (sallallâhu aleyhi ve sellem) \"Allahım, onun malını ve evladını çoğalt, ömrünü uzun kıl\" duasına mazhar olan Enes, en çok hadis rivayet eden yedi sahabiden (müksirûn) biri olarak sünnetin sonraki nesillere aktarılmasında kilit rol oynamıştır. İbadetlerindeki titizliği ve Peygamberimize olan benzerliğiyle bilinen bu büyük zat, bereketli bir ömrün ardından hicri 93 (m. 711-12) yılında, 103 yaşında Basra'da vefat eden son sahabi olarak Hakk'a yürümüştür.",
    description_ar: '',
    era_en: '612~712 CE',
    era_tr: '612~712 MS',
    title: "Prophet's Personal Attendant",
    title_tr: "Hâdimü'n-Nebî",
    title_ar: '',
    tags: [
      'Private Servant of the Prophet',
      'From the Muksirun (Prolific Narrators)',
      'From the Long-Lived',
    ],
    tags_tr: ['Peygamberimizin özel hizmetkârı', "Müksirûn'dan", "Muammerûn'dan"],
    tags_ar: [],
    idleVideo: buildVideoUrl('enes-malik', 'enes_bin_malik_idle.mp4'),
  },
  'muadh-jabal': {
    id: 'muadh-jabal',
    name: 'Hz. Muaz bin Cebel',
    name_en: 'Muadh ibn Jabal (RA)',
    name_ar: '',
    name_tr: 'Hz. Muaz bin Cebel (RA)',
    shortName: 'Muadh',
    fullName: 'Muadh ibn Jabal al-Khazraji',
    gender: 'male',
    image: buildImageUrl('muadh-jabal', 'muadh_bin_cebel.jpg'),
    images: buildResponsiveImageUrls('muadh-jabal', 'muadh_bin_cebel.jpg'),
    sahabeName: 'muaz_b_cebel_en',
    heygenAvatarId: 'f146764bc75245a49f46b1780c305f4e',
    heygenLiveAvatarId: '4c251081-d497-4c87-abed-b97653fb3b13',
    description:
      'Born in 603 CE, Mu\'adh embraced Islam young and was praised by the Messenger of God (peace and blessings be upon him) with the words: "The most knowledgeable of my community regarding what is lawful and unlawful is Mu\'adh." When the Prophet sent him to Yemen as both governor and teacher, he asked: "How will you judge?" Mu\'adh answered: "By the Book of God, then the Sunnah, then my own reasoning"—a response that pleased the Prophet deeply. Known for his generosity, beautiful character, and piety, he returned to the Levant after the Prophet\'s passing and served under Abu Ubaidah. He died in the plague of Amwas in 639 CE at the age of 38, leaving behind a legacy of knowledge and devotion.',
    description_tr:
      "Miladi 603 yılında Medine'de doğan Hz. Muaz, genç yaşta hidayetle şereflenmiş ve Resûlullah (sallallâhu aleyhi ve sellem) tarafından \"Ümmetimin helal ve haramı en iyi bileni Muaz'dır\" diye övülmüştür. Hz. Peygamber onu Yemen'e hem vali hem muallim olarak gönderirken \"Nasıl hükmedeceksin?\" diye sormuş, \"Allâh'ın Kitabı'yla, sonra Sünnet'le, sonra kendi içtihadımla\" cevabı Efendimizin yüzünü aydınlatmıştır. Cömertliği, güzel ahlâkı ve takvâsıyla tanınan Hz. Muaz, Peygamber'in vefatından sonra Şam bölgesine geçerek Ebû Ubeyde'nin yanında hizmet etmiştir. 639 yılında Amvâs taununda 38 yaşında vefat etmiş, ardında ilim ve ihlas dolu bir miras bırakmıştır.",
    description_ar: '',
    era_en: '603~639 CE',
    era_tr: '603~639 MS',
    title: 'Scholar of Islamic Law',
    title_tr: 'İslam Hukuku Alimi',
    title_ar: '',
    tags: ['Imam of the Jurists', "Teacher of the Qur'an", 'Generous Giver', 'Imam of Yemen'],
    tags_tr: ['Fakihlerin imamı', "Kur'ân Muallimi", 'İnfak Ehli', 'Yemen imamı'],
    tags_ar: [],
    idleVideo: buildVideoUrl('muadh-jabal', 'muadh_bin_cebel_idle.mp4'),
  },
  'sad-muadh': {
    id: 'sad-muadh',
    name: 'Hz. Saad bin Muaz',
    name_en: "Sa'd ibn Mu'adh (RA)",
    name_ar: '',
    name_tr: 'Hz. Saad bin Muaz (RA)',
    shortName: "Sa'd",
    fullName: "Sa'd ibn Mu'adh al-Aus",
    gender: 'male',
    image: buildImageUrl('sad-muadh', 'sad_bin_muaz.jpg'),
    images: buildResponsiveImageUrls('sad-muadh', 'sad_bin_muaz.jpg'),
    sahabeName: 'sad_b_muaz_en',
    heygenAvatarId: '606e7e290ed046998fb3a617ef3062e4',
    heygenLiveAvatarId: '5b492edb-9b84-4811-a28c-fc719fc57006',
    description:
      "Born in 590 CE, Sa'd ibn Mu'adh was a leading figure of the Ansar and a pillar of support for the Messenger of God (peace and blessings be upon him) in Medina. His acceptance of Islam, guided by Mus'ab ibn Umayr, became a turning point—his entire tribe, the Aws, followed him into the faith. At Badr, he was a shield of devotion, and at the Battle of the Trench, he was struck by an arrow yet continued to serve. After the siege, his judgment on the Banu Qurayzah was confirmed by divine approval. When he passed away in 627 CE at the age of 37, the Prophet said: \"The Throne of the All-Merciful trembled at the death of Sa'd ibn Mu'adh.\" Seventy thousand angels attended his funeral.",
    description_tr:
      "590 yılında doğan, Ensar'ın önde gelen lideri ve İslam'ın sarsılmaz kalesi Sa'd b. Muâz, Mus'ab b. Umeyr vesilesiyle Müslüman olmuş ve onun İslam'ı kabul etmesiyle birlikte Evs kabilesi toptan hidayetle şereflenmiştir. Bedir'de fedakârlığın timsali olan Sa'd, Hendek Savaşı'nda bir okla yaralanmış, buna rağmen hizmetinden geri kalmamıştır. Kuşatma sonrasında Benî Kurayza hakkında verdiği hüküm, ilahi tasdik ile doğrulanmıştır. 627 yılında 37 yaşında vefat ettiğinde Resûlullah (sallallâhu aleyhi ve sellem) \"Sa'd b. Muâz'ın ölümüyle Rahmân'ın Arş'ı titredi\" buyurmuştur. Cenazesine yetmiş bin melek katılmıştır.",
    description_ar: '',
    era_en: '590~627 CE',
    era_tr: '590~627 MS',
    title: 'Ansar Leader',
    title_tr: 'Ensar Lideri',
    title_ar: '',
    tags: ['Charismatic Leader', 'Loyal Advisor', 'Companion Who Shook the Throne'],
    tags_tr: ['Karizmatik Lider', 'Sadık danışman', "Arş'ı Titreten Sahabî"],
    tags_ar: [],
    idleVideo: buildVideoUrl('sad-muadh', 'sad_bin_muaz_idle.mp4'),
  },
  'suhayl-amr': {
    id: 'suhayl-amr',
    name: 'Hz. Süheyl b. Amr',
    name_en: 'Suhayl ibn Amr (RA)',
    name_ar: '',
    name_tr: 'Hz. Süheyl b. Amr (RA)',
    shortName: 'Suhayl',
    fullName: 'Suhayl ibn Amr al-Amiri',
    gender: 'male',
    image: buildImageUrl('suhayl-amr', 'suhayl_bin_amr.jpg'),
    images: buildResponsiveImageUrls('suhayl-amr', 'suhayl_bin_amr.jpg'),
    sahabeName: 'su_heyl_b_amr_en',
    heygenAvatarId: '9ad44eb36ec241bc8d9e9992f43beea7',
    heygenLiveAvatarId: 'a59f1393-c967-4436-9066-0b23a8c6165a',
    description:
      "A master orator and politically astute leader of Quraysh, Suhayl ibn 'Amr served as Mecca's spokesperson before Islam. He is especially known as Quraysh's chief negotiator at the Treaty of Hudaybiyyah. So powerful was his speech that after the Prophet's passing, he is credited with calming the people of Mecca through a decisive address. After accepting Islam following the Conquest of Mecca, he devoted himself to worship and to striving in God's cause, eager to make up for the past. He died in the Plague of 'Amwas in 18 AH (639 CE), in the Syrian region.",
    description_tr:
      "Doğum tarihi bilinmeyen Süheyl b. Amr, Kureyş'in en etkili hatiplerinden ve diplomatik zekâsıyla öne çıkan liderlerinden biridir. İslam öncesinde Mekke'nin sözcülüğünü üstlenen ve Hudeybiye Antlaşması'nda Kureyş tarafını temsil eden baş müzakereci olarak tarihe geçmiştir. Hitabeti o kadar güçlüdür ki, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) vefatından sonra Mekke halkını yatıştıran tarihi konuşmayı o yapmıştır. Mekke'nin fethinden sonra İslam'la şereflenen bu güzide sahabi, geçmişini telafi etmek arzusuyla kendisini ibadete ve cihad meydanlarına adamıştır. \"Allah yolunda harcanan bir saat, aile içinde geçirilen bir ömürden hayırlıdır\" diyerek cepheye koşan Süheyl, hicri 18 (miladi 639) yılında Şam bölgesindeki Amvas taununda vefat etmiştir.",
    description_ar: '',
    era_en: '560~639 CE',
    era_tr: '560~639 MS',
    title: 'Orator',
    title_tr: 'Hatip',
    title_ar: '',
    tags: ['Master of Words', 'Diplomat Companion', 'Man of Insight', 'Orator of Quraysh'],
    tags_tr: ['Söz Üstadı', 'Diplomat sahabi', 'Muhasebe İnsanı', "Kureyş'in hatibi"],
    tags_ar: [],
    idleVideo: buildVideoUrl('suhayl-amr', 'suhayl_bin_amr_idle.mp4'),
  },
  'zayd-thabit': {
    id: 'zayd-thabit',
    name: 'Hz. Zeyd bin Sâbit',
    name_en: 'Zayd ibn Thabit (RA)',
    name_ar: '',
    name_tr: 'Hz. Zeyd bin Sâbit (RA)',
    shortName: 'Zayd',
    fullName: 'Zayd ibn Thabit al-Ansari',
    gender: 'male',
    image: buildImageUrl('zayd-thabit', 'zayd_bin_sabit.jpg'),
    images: buildResponsiveImageUrls('zayd-thabit', 'zayd_bin_sabit.jpg'),
    sahabeName: 'zeyd_b_sabit_en',
    heygenAvatarId: '7d7ada12ef3f4b8fb155ce24ae7892f0',
    heygenLiveAvatarId: 'd5759fde-38d6-432c-b87a-6c7dfe1cfb16',
    description:
      "Born in Medina about eleven years before the Hijrah, Zayd ibn Thabit became one of the Prophet's distinguished scribes of revelation. With quick intelligence and a powerful memory, he learned languages such as Hebrew and Syriac in a short time and handled diplomatic correspondence. He was also regarded as a leading authority in inheritance law (fara'id). His most vital service was leading the committee that gathered the Qur'an's scattered written materials into an organized Mushaf. This work was pivotal in preserving the Qur'an's text through the generations. He died in 45 AH at the age of 56.",
    description_tr:
      'Hicret\'ten yaklaşık on bir yıl önce Medine\'de doğan Zeyd bin Sâbit, kıvrak zekâsı ve güçlü hafızasıyla Peygamber Efendimizin (sallallâhu aleyhi ve sellem) hususi "Vahiy Kâtibi" olmuş seçkin bir sahabidir. İbranice ve Süryanice gibi yabancı dilleri sayılı günlerde öğrenip devletin diplomatik yazışmalarını yürüten Zeyd, miras hukuku (ferâiz) alanında ümmetin en bilgini kabul edilir. Onun asıl alâmet-i farikası ve en hayati hizmeti ise, Kur\'an-ı Kerim\'in dağınık parçalarını toplayıp kitap (Mushaf) haline getiren heyete başkanlık etmesidir. Kutsal kitabımızın değişmeden bugüne ulaşmasında kilit rol oynayan ve "Ümmetin âlimi" vasfıyla anılan bu büyük değer, Hicri 45 yılında, 56 yaşındayken vefat etmiştir.',
    description_ar: '',
    era_en: '610~656 CE',
    era_tr: '610~656 MS',
    title: 'Scribe of Revelation',
    title_tr: 'Vahiy Katibi',
    title_ar: '',
    tags: ['Scribe of Revelation', "Compiler of the Qur'an", 'Scholar Companion', 'Translator'],
    tags_tr: ['Vahiy Kâtibi', "Kur'ân'ı Toplayan", 'Alim sahabi', 'Tercüman'],
    tags_ar: [],
    idleVideo: buildVideoUrl('zayd-thabit', 'zayd_bin_sabit_idle.mp4'),
  },
  'abu-ubaidah': {
    id: 'abu-ubaidah',
    name: 'Hz. Ebû Ubeyde İbn-i Cerrâh',
    name_en: 'Abu Ubaydah ibn al-Jarrah (RA)',
    name_ar: '',
    name_tr: 'Hz. Ebû Ubeyde İbn-i Cerrâh (RA)',
    shortName: 'Abu Ubaidah',
    fullName: 'Abu Ubaidah ibn al-Jarrah al-Yamani',
    gender: 'male',
    image: buildImageUrl('abu-ubaidah', 'abu_ubaidah_jarrah.jpg'),
    images: buildResponsiveImageUrls('abu-ubaidah', 'abu_ubaidah_jarrah.jpg'),
    sahabeName: 'ebu_ubeyde_b_cerrah_en',
    heygenAvatarId: '9105e5b339de466c90a3b74402d963c7',
    heygenLiveAvatarId: 'b25557d0-2bfa-4fc2-814d-ac35ffd0351d',
    description:
      "Born in Mecca in 583 CE, Abu 'Ubaydah is celebrated as a symbol of trustworthiness. The Prophet Muhammad (peace and blessings be upon him) is reported to have said: \"Every nation has a trustee; the trustee of this nation is Abu 'Ubaydah.\" At Uhud, he showed extraordinary devotion—removing links of the Prophet's armor from his face with his own teeth, sacrificing his front teeth in the process. One of the ten Companions promised Paradise, he was known for humility, asceticism, and gentleness. Despite commanding armies in the conquests, he lived simply and avoided worldly accumulation. He died in 639 CE during the Plague of 'Amwas, at about 58 years of age.",
    description_tr:
      "583 yılında Mekke'de doğmuştur. O, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) \"Her ümmetin bir emini vardır, bu ümmetin emini de Ebû Ubeyde'dir\" iltifatına mazhar olmuş, güvenilirliğiyle simgeleşmiş bir sahabidir. Uhud'da Efendimizin yüzüne batan miğfer halkalarını dişleriyle çıkarırken ön dişlerini feda edecek kadar fedakârdır.",
    description_ar: '',
    era_en: '583~639 CE',
    era_tr: '583~639 MS',
    title: 'Trustee of the Nation',
    title_tr: "Millet'in Emini",
    title_ar: '',
    tags: ['Trustee of the Ummah', 'Ascetic Companion', 'Hero of Uhud'],
    tags_tr: ['Ümmetin emini', 'Zâhid sahabi', 'Hetemlerin en güzeli'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abu-ubaidah', 'abu_ubaidah_jarrah_idle.mp4'),
  },
  'abdullah-rawahah': {
    id: 'abdullah-rawahah',
    name: 'Hz. Abdullah bin Revâha',
    name_en: 'Abdullah ibn Rawahah (RA)',
    name_ar: '',
    name_tr: 'Hz. Abdullah bin Revâha (RA)',
    shortName: 'Ibn Rawahah',
    fullName: 'Abdullah ibn Rawahah al-Ansari',
    gender: 'male',
    image: buildImageUrl('abdullah-rawahah', 'abdullah_bin_rawahah.jpg'),
    images: buildResponsiveImageUrls('abdullah-rawahah', 'abdullah_bin_rawahah.jpg'),
    sahabeName: 'abdulla_h_b_revaha_en',
    heygenAvatarId: '2e09135d84ad47608a79120d4597caeb',
    heygenLiveAvatarId: '68676dca-af61-42d5-94d5-7c6be95eaf9a',
    description:
      "Born in Medina, 'Abdullah ibn Rawahah was one of the rare literate men of the pre-Islamic era. He served as a scribe of revelation for the Messenger of God (peace and blessings be upon him) and stood out as a remarkable poet, who defended Islam with both pen and sword. The Prophet is reported to have said that his poetry struck the hearts of the polytheists more powerfully than arrows. He was known for unquestioning obedience, deep piety, and the habit of calling his fellow Companions away from worldly distraction with the words, \"Come—let's renew our faith for an hour.\" As the third commander at Mu'ta, he took up the banner and fought with extraordinary courage, attaining martyrdom there in 629 CE.",
    description_tr:
      "Abdullah bin Revâha Medine'de dünyaya gelmiş, Cahiliye devrinin nadir okur-yazarlarından biri olarak Peygamber Efendimizin (sallallâhu aleyhi ve sellem) vahiy kâtipliğini yapmış, keskin kalemi ve kılıcıyla İslam'ı savunmuş müstesna bir şair sahabidir. Resûlullah'ın, şiirlerinin müşrikler üzerinde oktan daha tesirli olduğunu müjdelediği bu yiğit zat, emirlere tereddütsüz itaati ve derin takvasıyla tanınır. Onun en belirgin özelliklerinden biri, ashabı \"Gel, bir saat iman edelim\" diyerek dünya telaşından uzaklaşıp zikir ve tefekkure davet etmesidir. İslam ordusunun üçüncü komutanı olarak sancağı devraldığı Mûte'de, Miladi 629 yılında kahramanca mücadele ederek şehid olmuştur.",
    description_ar: '',
    era_en: '580~629 CE',
    era_tr: '580~629 MS',
    title: 'Poet',
    title_tr: 'Şair',
    title_ar: '',
    tags: ['Scribe of Revelation', 'Poet Companion', 'People of Dhikr', 'Obedient Companion'],
    tags_tr: ['Vahiy kâtibi', 'Şair sahabi', 'Zikir Ehli', 'İtaatkâr sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abdullah-rawahah', 'abdullah_bin_rawahah_idle.mp4'),
  },
  'abu-talhah': {
    id: 'abu-talhah',
    name: 'Hz. Ebû Talha el-Ensârî',
    name_en: 'Abu Talhah al-Ansari (RA)',
    name_ar: '',
    name_tr: 'Hz. Ebû Talha el-Ensârî (RA)',
    shortName: 'Abu Talhah',
    fullName: 'Zayd ibn Sahl al-Ansari',
    gender: 'male',
    image: buildImageUrl('abu-talhah', 'abu_talhah_ansari.jpg'),
    images: buildResponsiveImageUrls('abu-talhah', 'abu_talhah_ansari.jpg'),
    sahabeName: 'ebu_talha_en',
    heygenAvatarId: '4a736a3e544d495c836e55f9eea6517d',
    heygenLiveAvatarId: 'c0427579-027f-486e-a906-4036638f4d82',
    description:
      'Born in Medina before Islam, Abu Talhah was a fearless Companion with a thunderous voice—so much so that the Messenger of God (peace and blessings be upon him) praised him, saying his voice in the army was better than a thousand men. In battle, he famously shielded the Prophet with his own body and is remembered for the words, "May my life be a ransom for yours; my face is a shield for your face." One of the most striking signs of his surrender to God was his marriage to Umm Sulaym, who made his acceptance of Islam her bridal gift. Renowned for generosity, he is also linked to the Qur\'anic passage revealed about those who prefer others over themselves—after he sacrificed his children\'s food to honor a guest. Abu Talhah devoted his life to Islam and, though advanced in age, joined the Cyprus expedition. He died aboard a ship in 34 AH (654 CE), attaining the rank of martyrdom.',
    description_tr:
      'Câhiliye devrinde Medine\'de doğan Ebû Talha, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) "Sesi orduda bin kişiden daha hayırlıdır" iltifatına mazhar olmuş gür sesli ve yiğit bir sahabidir. O, savaş meydanlarında vücudunu Peygamber Efendimize (sallallâhu aleyhi ve sellem) siper etmesi ve "Canım canına feda, yüzüm yüzünün kalkanıdır" sözüyle hafızalara kazınmıştır. İslamiyet\'i kabul etmesini mehr sayan Ümmü Süleym ile yaptığı evlilik ise onun teslimiyetinin en güzel nişanesidir. Misafirini doyurmak için çocuklarının rızkını feda etmesi üzerine hakkında ayet inen bu cömert insan, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) kendisine "dayı" diye iltifat ettiği bir yakınıydı. Ömrünü İslam\'a hizmete adayan Ebû Talha, oldukça yaşlı bir haldeyken katıldığı Kıbrıs seferinde, Hicri 34 (Miladi 654) yılında gemide vefat ederek şehadet mertebesine erişmiştir.',
    description_ar: '',
    era_en: '575~654 CE',
    era_tr: '575~654 MS',
    title: 'Warrior',
    title_tr: 'Savaşçı',
    title_ar: '',
    tags: ['Valiant Companion', 'Generous Companion', 'Warrior Companion'],
    tags_tr: ['Yiğit sahabi', 'Cömert sahabi', 'Cengâver sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abu-talhah', 'abu_talhah_ansari_idle.mp4'),
  },
  'abdullah-jaafar': {
    id: 'abdullah-jaafar',
    name: "Hz. Abdullah b. Ca'fer b. Ebi Tâlib",
    name_en: "Abdullah ibn Ja'far ibn Abi Talib (RA)",
    name_ar: '',
    name_tr: "Hz. Abdullah b. Ca'fer b. Ebi Tâlib (RA)",
    shortName: "Ibn Ja'far",
    fullName: "Abdullah ibn Ja'far ibn Abi Talib",
    gender: 'male',
    image: buildImageUrl('abdullah-jaafar', 'abdullah_bin_jaafar.jpg'),
    images: buildResponsiveImageUrls('abdullah-jaafar', 'abdullah_bin_jaafar.jpg'),
    sahabeName: 'abdullah_b_cafer_b_ebi_talib_en',
    heygenAvatarId: '6516bb8db7f043dc8505b47a8b015344',
    heygenLiveAvatarId: 'e1199012-48e3-406b-894e-ab307a2807f7',
    description:
      '\'Abdullah ibn Ja\'far holds the distinction of being the first Muslim child born in Abyssinia. After his father was martyred, he was raised under the personal care of the Messenger of God (peace and blessings be upon him). He received the Prophet\'s special affection and is remembered for the words, "In character and appearance, you resemble me," along with a personal prayer for blessing in his trade. Above all, he became legendary for boundless generosity—earning titles such as "the Ocean of Generosity" and "the Pole of Generosity." Known for sharing whatever he had with others, he passed away in Medina at the age of about 90, in the 80th year after the Hijrah.',
    description_tr:
      'Habeşistan\'da dünyaya gelen ilk Müslüman çocuk olma şerefini taşıyan Abdullah, babası şehit düşünce bizzat Peygamber Efendimizin (sallallâhu aleyhi ve sellem) himayesinde büyümüştür. O, Efendimiz\'in "Huyu ve yaratılışı bana benzer" iltifatına ve ticaretinin bereketlenmesi yönündeki özel duasına mazhar olan bahtiyar bir isimdir. Tarihteki en bariz vasfı ise sınır tanımayan cömertliğidir. "Cömertlik Okyanusu" (Bahru\'l-cûd) ve "Cömertlik Kutbu" lakaplarıyla anılan bu zarafet timsali sahabi, varlığını insanlarla paylaşmayı hayat felsefesi edinmiştir. Medine\'de, Hicret\'in 80. yılında, yaklaşık 90 yaşında vefat etmiştir.',
    description_ar: '',
    era_en: '616~700 CE',
    era_tr: '616~700 MS',
    title: 'Philanthropist',
    title_tr: 'Hayırseç',
    title_ar: '',
    tags: ['Pinnacle in Generosity', 'Lookalike of the Prophet', 'Animal Lover'],
    tags_tr: ['Cömertlikte zirve', 'Peygamber Benzeri', 'Hayvansever sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abdullah-jaafar', 'abdullah_bin_jaafar_idle.mp4'),
  },
  'safwan-umayyah': {
    id: 'safwan-umayyah',
    name: 'Hz. Safvân b. Ümeyye',
    name_en: 'Safwan ibn Umayyah (RA)',
    name_ar: '',
    name_tr: 'Hz. Safvân b. Ümeyye (RA)',
    shortName: 'Safwan',
    fullName: 'Safwan ibn Umayyah al-Jumahi',
    gender: 'male',
    image: buildImageUrl('safwan-umayyah', 'safwan_bin_umayyah.jpg'),
    images: buildResponsiveImageUrls('safwan-umayyah', 'safwan_bin_umayyah.jpg'),
    sahabeName: 'safvan_b_umeyye_en',
    heygenAvatarId: 'cd2bfdce8abc4ab5ba0e184b892714e4',
    heygenLiveAvatarId: '00864817-d95d-4663-bb2d-5aff4dbdb11c',
    description:
      "A prominent Quraysh leader celebrated for eloquence and leadership, Safwan initially resisted Islam. However, after the Messenger of God (peace and blessings be upon him) granted him a four-month period to reflect—and after witnessing the Prophet's unmatched generosity at Hunayn—he embraced faith. One of the most striking episodes in his life is that even before becoming Muslim, he supplied weapons and armor to the Muslim army on the basis of trust. Counted among those whose hearts were to be reconciled and known for openhandedness, he remained in his homeland after the conquest since migration was no longer required. He lived thereafter as a sincere believer and died in Mecca in 41 AH (661 CE).",
    description_tr:
      "Kureyş'in önde gelenlerinden olup hitabeti ve liderliğiyle tanınan Safvân, başlangıçta İslam'a dirense de Peygamber Efendimizin (sallallâhu aleyhi ve sellem) kendisine tanıdığı dört aylık düşünme mühleti ve Huneyn'deki eşsiz cömertliği karşısında iman etmiştir. Henüz Müslüman değilken, güvene dayalı olarak İslam ordusuna ödünç silah ve zırh temin etmesi onun en dikkat çekici özelliklerinden biridir. \"Müellefe-i kulûb\"dan sayılan ve ikramseverliğiyle bilinen bu sahabi, fetihten sonra hicret zorunluluğu kalktığı için memleketinde yaşamaya devam etmiştir. Hayatını samimi bir mümin olarak sürdüren Safvân, Hicri 41 (Miladi 661) yılında Mekke'de vefat etmiştir.",
    description_ar: '',
    era_en: '560~661 CE',
    era_tr: '560~661 MS',
    title: 'Quraysh Leader',
    title_tr: "Kurey' Lideri",
    title_ar: '',
    tags: ['From Those Whose Hearts Were Reconciled', 'Generous Companion', 'Eloquent Arbiter'],
    tags_tr: ["Müellefe-i kulûb'dan", 'Cömert sahabi', 'Fesahatli Hakem'],
    tags_ar: [],
    idleVideo: buildVideoUrl('safwan-umayyah', 'safwan_bin_umayyah_idle.mp4'),
  },
  'ikrimah-jahl': {
    id: 'ikrimah-jahl',
    name: 'Hz. İkrime bin Ebî Cehil',
    name_en: 'Ikrimah ibn Abi Jahl (RA)',
    name_ar: '',
    name_tr: 'Hz. İkrime bin Ebî Cehil (RA)',
    shortName: 'Ikrimah',
    fullName: 'Ikrimah ibn Abi Jahl al-Makhzumi',
    gender: 'male',
    image: buildImageUrl('ikrimah-jahl', 'ikrimah_bin_jahl.jpg'),
    images: buildResponsiveImageUrls('ikrimah-jahl', 'ikrimah_bin_jahl.jpg'),
    sahabeName: 'i_krime_b_ebi_cehl_en',
    heygenAvatarId: 'ff3248666fc440d9867e866d5a9143b6',
    heygenLiveAvatarId: 'fa047d65-2002-4647-8e3b-aa181a5eb154',
    description:
      'Once the son of Islam\'s fiercest enemy, \'Ikrimah was guided to faith and was welcomed by the Messenger of God (peace and blessings be upon him) with the words, "Welcome, rider of the Hijrah!" After the conquest of Mecca—through the efforts of his wife—he accepted Islam and vowed that whatever he had spent against Islam, he would spend twice as much in its cause. He was known for a tender heart that would cling to the Qur\'an and weep, saying, "The Word of my Lord!" Yet he was also a warrior of legendary bravery, rushing from battlefield to battlefield to redeem his past. He fulfilled his vow at Yarmuk, where he was martyred in 636 CE, at around 62 years of age.',
    description_tr:
      "İslam'ın en yaman düşmanının oğlu iken hidayetle şereflenen ve Peygamber Efendimizin (sallallâhu aleyhi ve sellem) kendisine kucak açıp \"Hoş geldin ey hicret süvarisi!\" iltifatıyla karşıladığı mücahit sahabidir,. Mekke'nin fethi sonrası eşinin gayretiyle Müslüman olmuş, o güne kadar İslam aleyhine harcadığı her imkânın iki katını o günden sonra Hak yolunda harcamaya yemin etmiştir. Kur'ân-ı Kerim'i bağrına basıp \"Rabbimin kelamı!\" diyerek gözyaşı döken bu hassas gönül, aynı zamanda cesaretiyle destanlaşan bir cengâverdir. Geçmişini telafi etme azmiyle cepheden cepheye koşan Hz. İkrime, Yermük'te 636 tarihinde 62 yaşında şehadet şerbetini içerek verdiği sözü tutmuş ve ebedi âleme göçmüştür.",
    description_ar: '',
    era_en: '614~636 CE',
    era_tr: '614~636 MS',
    title: 'Warrior',
    title_tr: 'Savaşçı',
    title_ar: '',
    tags: ['Rider of the Hijrah', "Lover of the Qur'an", 'Warrior Companion'],
    tags_tr: ['Hicret süvarisi', "Kur'ân Sevdalısı", 'Cengâver sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('ikrimah-jahl', 'ikrimah_bin_jahl_idle.mp4'),
  },
  'halimah-sadiyyah': {
    id: 'halimah-sadiyyah',
    name: "Hz. Halime bint Züeyb es-Sa'diyye",
    name_en: "Halimah bint Abi Dhu'ayb al-Sa'diyyah (RA)",
    name_ar: '',
    name_tr: "Hz. Halime bint Züeyb es-Sa'diyye (RA)",
    shortName: 'Halimah',
    fullName: "Halimah bint Abi Dhu'ayb al-Sa'diyyah",
    gender: 'female',
    image: buildImageUrl('halimah-sadiyyah', 'halimah_sadiyyah.jpg'),
    images: buildResponsiveImageUrls('halimah-sadiyyah', 'halimah_sadiyyah.jpg'),
    sahabeName: 'halime_sadiyye_en',
    heygenAvatarId: 'e18d4109feb345a8a39fd929722cdf1a',
    heygenLiveAvatarId: '2fb63549-cc99-4504-8cb8-d36197a22ada',
    description:
      "From the Banu Sa'd branch of Hawazin, Halimah was the Prophet Muhammad's (peace and blessings be upon him) wet nurse. True to her name, she was known for gentleness, loyalty, and compassion. The Prophet grew up among her people, who were famed for speaking Arabic with great purity and eloquence, and he later praised their language. During a time of drought and hardship, her household and livestock were said to become unexpectedly blessed with abundance once she took the infant Prophet into her care. Years later, when the Prophet met her again, he spread his cloak for her to sit upon—an unforgettable sign of gratitude and loyalty. It is reported that she died before the conquest of Mecca and was buried in Jannat al-Baqi' in Medina.",
    description_tr:
      "Hevazin kabilesinin Sa'd koluna mensup olan Halime, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) sütannesidir. İsmiyle müsemma şekilde \"halim\" (yumuşak huylu) bir karaktere sahip olup, emanete sadakati ve şefkatiyle tanınır. Peygamber Efendimiz (sallallâhu aleyhi ve sellem), o dönemde Arapçayı en duru konuşan bu kabilede büyümüş, \"Ben Sa'd'da emzirildim\" diyerek onların fasih lisanını övmüştür. Evi ve binekleri kuraklık yüzünden verimsizken, Peygamber Efendimizi (sallallâhu aleyhi ve sellem) yanına almasıyla hanesi görülmemiş bir berekete ve bolluğa kavuşmuştur. Yıllar sonra sütannesini gördüğünde ridasını yere sererek ona büyük bir hürmet gösteren Efendimizin bu vefa dolu tavrı, tarihin en güzel sadakat tablolarından biridir. Mekke'nin fethinden önce vefat ettiği ve Medine'deki Cennetü'l-Bakî'ye defnedildiği rivayet edilmektedir.",
    description_ar: '',
    era_en: '570~615 CE',
    era_tr: '570~615 MS',
    title: 'Wet Nurse',
    title_tr: 'Süt Annesi',
    title_ar: '',
    tags: ['Wet Nurse of the Prophet', 'Gentle Female Companion', 'Compassionate Trustee'],
    tags_tr: ['Peygamberimizin sütannesi', 'Halim kadın sahabi', 'Şefkatli Emanetdar'],
    tags_ar: [],
    idleVideo: buildVideoUrl('halimah-sadiyyah', 'halimah_sadiyyah_idle.mp4'),
  },
  'abdullah-amr': {
    id: 'abdullah-amr',
    name: 'Hz. Abdullah b. Amr b. el-Âs',
    name_en: 'Abdullah ibn Amr ibn al-As (RA)',
    name_ar: '',
    name_tr: 'Hz. Abdullah b. Amr b. el-Âs (RA)',
    shortName: 'Ibn Amr',
    fullName: 'Abdullah ibn Amr ibn al-As al-Sahmi',
    gender: 'male',
    image: buildImageUrl('abdullah-amr', 'abdullah_bin_amr.jpg'),
    images: buildResponsiveImageUrls('abdullah-amr', 'abdullah_bin_amr.jpg'),
    sahabeName: 'abdullah_b_amr_b_el-as_en',
    heygenAvatarId: '10e743cd26f64925a2cdaf54692a3c15',
    heygenLiveAvatarId: 'fdc977fc-73c9-41da-b8a3-df5076b5c08e',
    description:
      "Born around 615 CE, 'Abdullah ibn 'Amr accepted Islam before his father and became known as a lover of knowledge. He is especially remembered for writing down hadith with the Prophet's (peace and blessings be upon him) explicit permission—recording what he heard immediately and preserving it in a collection known as al-Sahifah al-Sadiqah (The Truthful Script). This made him a pioneering figure in the written preservation of the Sunnah. Literate, intellectually curious, and able to read earlier scriptures (and reportedly Syriac), he was also deeply devoted to worship—so much so that the Prophet repeatedly advised him not to overburden himself. He passed away in Egypt around 65 AH (685 CE), at about 72 years old.",
    description_tr:
      'Miladi 615 yılı dolaylarında doğan Abdullah, babasından önce İslam\'la şereflenmiş ilim aşığı bir sahabidir. O daha çok, okuma yazma bilmesi ve bizzat Peygamber Efendimizin (sallallâhu aleyhi ve sellem) özel izniyle duyduğu hadisleri anında yazarak kayıt altına almasıyla bilinir. Oluşturduğu ve "es-Sahîfetü\'s-Sâdıka" ismini verdiği bu yazılı metinlerle, sünnetin sonraki nesillere güvenle aktarılmasında tarihe geçen öncü bir rol üstlenmiştir. Süryanice bilen ve eski kutsal kitapları da okuyan entelektüel bir birikime sahip olan Abdullah, aynı zamanda hayatını ibadete adamış bir "âbid" idi. Gündüzlerini oruçla, gecelerini namazla geçirecek kadar yüksek bir takvaya sahip olduğundan Peygamber Efendimiz (sallallâhu aleyhi ve sellem) tarafından vücudunu yormaması için sık sık uyarılmıştır. Ömrünü ilim ve ibadetle yoğuran bu mümtaz şahsiyet, Hicri 65 (Miladi 685) civarında, 72 yaşında Mısır\'da rahmet-i Rahman\'a kavuşmuştur.',
    description_ar: '',
    era_en: '615~685 CE',
    era_tr: '615~685 MS',
    title: 'Hadith Scholar',
    title_tr: 'Hadis Alimi',
    title_ar: '',
    tags: ['Devout Companion', 'Scribe of Hadith', 'Enlightened Scholar', 'Versatile Companion'],
    tags_tr: ['Âbid sahabi', 'Hadis Kâtibi', 'Münevver Âlim', 'Çok yönlü sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abdullah-amr', 'abdullah_bin_amr_idle.mp4'),
  },
  'abdullah-zubayr': {
    id: 'abdullah-zubayr',
    name: 'Hz. Abdullah bin Zübeyr',
    name_en: 'Abdullah ibn al-Zubayr (RA)',
    name_ar: '',
    name_tr: 'Hz. Abdullah bin Zübeyr (RA)',
    shortName: 'Ibn al-Zubayr',
    fullName: 'Abdullah ibn al-Zubayr ibn al-Awwam',
    gender: 'male',
    image: buildImageUrl('abdullah-zubayr', 'abdullah_bin_zubayr.jpg'),
    images: buildResponsiveImageUrls('abdullah-zubayr', 'abdullah_bin_zubayr.jpg'),
    sahabeName: 'abdullah_b_zu_beyr_b_avvam_en',
    heygenAvatarId: 'f47603392d5f4deca43b81e501c23f7b',
    heygenLiveAvatarId: '966d23fc-862e-4cc0-af31-0845df247756',
    description:
      'Born in Medina in May 624 CE, \'Abdullah ibn al-Zubayr holds a special place in Islamic history as the first child born to the Muhajirun after the Hijrah—an event celebrated as a sign of hope and blessing. The Messenger of God (peace and blessings be upon him) personally named him, performed "tahnik" (rubbing the palate of a newborn baby with honey, sweet juice or pressed dates) for him, and prayed for him. Known for intense devotion and extraordinary humility in Prayer, he was nicknamed "the Pigeon of the Mosque." He also became famous for courage, eloquence, and an unyielding commitment to justice. A symbol of honorable resistance, he was martyred near the Ka\'ba in October 692 CE, at around 72 years old.',
    description_tr:
      "Mayıs 624'te Medine'de doğan Abdullah bin Zübeyr, Hicret sonrası dünyaya gelen ilk muhacir çocuğu olmasıyla İslam tarihinde müjdelenmiş eşsiz bir yere sahiptir. Peygamber Efendimizin (sallallâhu aleyhi ve sellem) bizzat ismini koyduğu, tahnik yaptığı ve hayır duada bulunduğu bu güzide sahabi, ibadete olan derin düşkünlüğü ve namazdaki olağanüstü huşusu sebebiyle \"Mescid Güvercini\" lakabıyla anılmıştır. Cesareti, fasih hitabeti ve adalet konusundaki tavizsiz duruşuyla tanınan Abdullah, hayatı boyunca haksızlığa karşı direnişin ve onurlu bir duruşun sembolü olmuştur. Gür sesi, ilmi ve takvasıyla müminlerin kalbinde yer edinen bu büyük lider, Kabe'nin gölgesinde hak bildiği yolda mücadele ederken Ekim 692 yılında, 72 yaşında şehit edilmiştir.",
    description_ar: '',
    era_en: '624~692 CE',
    era_tr: '624~692 MS',
    title: 'Devout Companion',
    title_tr: 'Dindar Sahabi',
    title_ar: '',
    tags: ['Pigeon of the Mosque', 'Horseman of Quraysh', 'Courageous Companion'],
    tags_tr: ['Mescid Güvercini', "Kureyş'in süvarisi", 'Cesur sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abdullah-zubayr', 'abdullah_bin_zubayr_idle.mp4'),
  },
  'amr-umayyah': {
    id: 'amr-umayyah',
    name: 'Hz. Amr b. Ümeyye ed-Damrî',
    name_en: 'Amr ibn Umayyah al-Damri (RA)',
    name_ar: '',
    name_tr: 'Hz. Amr b. Ümeyye ed-Damrî (RA)',
    shortName: 'Amr',
    fullName: 'Amr ibn Umayyah al-Damri',
    gender: 'male',
    image: buildImageUrl('amr-umayyah', 'amr_bin_umayyah.jpg'),
    images: buildResponsiveImageUrls('amr-umayyah', 'amr_bin_umayyah.jpg'),
    sahabeName: 'amr_b_u_meyye_en',
    heygenAvatarId: 'eea75aee9d0944c8828c3f00bac70b07',
    heygenLiveAvatarId: 'f90a6efb-2ef4-4485-8b01-cc527dd296ba',
    description:
      "One of the early diplomats of Islam—celebrated for courage, intelligence, and eloquence—'Amr ibn Umayyah carried the title \"the messenger of the Prophet\" (Sa'i Rasulillah) for his special envoys' missions. Known for boldness even before Islam, he became a trusted figure for the most sensitive interstate assignments, aided by his established relationship with the Negus of Abyssinia. He was famed for exceptional speed and agility, to the point that even horses were said to struggle to catch him. Among his notable roles were representing Umm Habibah in her marriage proceedings and retrieving the body of Khubayb from enemy territory. He is reported to have died in Medina at around 85 years old (d. 680 CE).",
    description_tr:
      'İslam tarihinin cesareti, zekâsı ve hitabetiyle parlayan ilk diplomatı ve "Sâî Rasûlillah" unvanıyla Peygamber Efendimizin (sallallâhu aleyhi ve sellem) özel elçisidir. Câhiliye döneminde gözü pekliğiyle tanınan bu yiğit sahabi, Habeş Necaşîsi ile olan eski dostluğu ve güvenilirliği sayesinde en kritik devletlerarası görevleri başarıyla üstlenmiştir.',
    description_ar: '',
    era_en: '575~680 CE',
    era_tr: '575~680 MS',
    title: 'Diplomat',
    title_tr: 'Diplomat',
    title_ar: '',
    tags: ['Special Envoy of the Prophet', 'The First Diplomat of Islam', 'Courageous Companion'],
    tags_tr: ['Peygamberin Özel Elçisi', "İslam'ın ilk diplomatı", 'Cesur sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('amr-umayyah', 'amr_bin_umayyah_idle.mp4'),
  },
  'sad-waqqas': {
    id: 'sad-waqqas',
    name: "Hz. Sa'd b. Ebî Vakkâs",
    name_en: "Sa'd ibn Abi Waqqas (RA)",
    name_ar: '',
    name_tr: "Hz. Sa'd b. Ebî Vakkâs (RA)",
    shortName: "Sa'd",
    fullName: "Sa'd ibn Abi Waqqas al-Zuhri",
    gender: 'male',
    image: buildImageUrl('sad-waqqas', 'sad_bin_waqqas.jpg'),
    images: buildResponsiveImageUrls('sad-waqqas', 'sad_bin_waqqas.jpg'),
    sahabeName: 'sad_b_ebi_vakkas_en',
    heygenAvatarId: 'fd2269ef3b654662a83ab82102052da0',
    heygenLiveAvatarId: 'dfd42165-df75-4575-b25e-d63ebe76c309',
    description:
      "Born in Mecca in 592 CE, Sa'd was among the earliest Muslims and one of the ten Companions promised Paradise. Because of family ties on the Prophet's mother's side, the Prophet (peace and blessings be upon him) proudly called him \"my uncle.\" Sa'd is remembered as the first to shoot an arrow in the path of Islam and is often referred to as \"the Knight of Islam.\" He remained firm despite intense pressure from his mother and was known as someone whose prayers were answered. Avoiding civil strife later in life, he preserved his neutrality during times of turmoil. The conqueror of Persia and founder of Kufa, he died in 675 CE in the valley of al-'Aqiq, around 83 years old.",
    description_tr:
      "592 yılında Mekke'de doğan Sa'd, İslam'ı ilk kabul edenlerden ve cennetle müjdelenen meşhur on sahabiden biridir. Peygamber Efendimizin (sallallâhu aleyhi ve sellem) anne tarafından akrabası olduğu için kendisine gururla \"Dayım\" diye hitap ettiği bu güzide sahabi, İslam uğruna ilk oku atan kahraman ve \"Farisu'l-İslam\" unvanıyla tarihe geçmiştir. Annesinin tüm baskılarına rağmen inancından taviz vermeyen Sa'd, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) özel iltifatına mazhar olduğu için duaları geri çevrilmeyen biri olarak tanınırdı. Fitne zamanlarında kargaşadan uzak durarak tarafsızlığını koruyan, İran fatihi ve Kûfe'nin kurucusu olan bu büyük komutan, 675 yılında Akik vadisinde yaklaşık 83 yaşında vefat etmiştir.",
    description_ar: '',
    era_en: '592~675 CE',
    era_tr: '592~675 MS',
    title: 'Knight of Islam',
    title_tr: "İslam'ın Şövalyesi",
    title_ar: '',
    tags: [
      'From the Ten Promised Paradise',
      'Military Genius',
      'Companion with Accepted Prayers',
      'Valiant Companion',
    ],
    tags_tr: ["Aşere-i Mübeşşere'den", 'Askeri dahi', 'Duası makbul sahabi', 'Yiğit sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('sad-waqqas', 'sad_bin_waqqas_idle.mp4'),
  },
  'said-zayd': {
    id: 'said-zayd',
    name: 'Hz. Said b. Zeyd',
    name_en: "Sa'id ibn Zayd (RA)",
    name_ar: '',
    name_tr: 'Hz. Said b. Zeyd (RA)',
    shortName: "Sa'id",
    fullName: "Sa'id ibn Zayd al-Adawi",
    gender: 'male',
    image: buildImageUrl('said-zayd', 'said_bin_zayd.jpg'),
    images: buildResponsiveImageUrls('said-zayd', 'said_bin_zayd.jpg'),
    sahabeName: 'said_b_zeyd_en',
    heygenAvatarId: '514c1f9ba4ca48288be1a4246361435e',
    heygenLiveAvatarId: 'caaf69b1-bec6-4b5b-8d5f-9cf0b93ab260',
    description:
      "Born around 590–600 CE, Sa'id ibn Zayd was one of the ten Companions promised Paradise and among the earliest callers to Islam. He is also remembered as a faithful, discerning figure who helped pave the way for 'Umar's acceptance of Islam. Carrying the spirit of the Hanif tradition inherited from his father, he rushed toward truth with sincerity and spent his youth in service. He avoided worldly status and fame, living an ascetic, God-fearing life, and was known as someone whose prayers were accepted. Quiet and dignified in demeanor, he nonetheless showed striking courage and wisdom when defending the truth. He died in Medina in 51 AH, in his 70's.",
    description_tr:
      "Yaklaşık 590-600 yılları arasında doğan Said b. Zeyd, Peygamber Efendimizin (sallallâhu aleyhi ve sellem) hayattayken cennetle müjdelediği on seçkin sahabeden (Aşere-i Mübeşşere) biridir. O, İslam'ın ilk davetçilerinden olup eniştesi Hz. Ömer'in Müslüman olmasına vesile olan inançlı ve ferasetli bir şahsiyettir. Babasından miras kalan Hanif ruhuyla hakikate koşmuş, gençliğinin tüm enerjisini İslam'a hizmetle geçirmiştir. Dünyevi makamlardan ve şöhretten kaçınan, zühd ve takva sahibi bir hayat süren Said, \"duası makbul\" bir zat olarak bilinir. Sessiz ve vakur duruşunun ardında, hakkı savunurken gösterdiği eşsiz bir cesaret ve zekâ saklıdır. Örnek hayatıyla gönüllerde yer eden bu büyük sahabi, hicri 51 yılında yetmiş küsur yaşlarında Medine'de vefat etmiştir.",
    description_ar: '',
    era_en: '590~672 CE',
    era_tr: '590~672 MS',
    title: 'Pioneer',
    title_tr: 'Öncü',
    title_ar: '',
    tags: ['From the Ten Promised Paradise', 'Ascetic Companion', 'Courageous Companion'],
    tags_tr: ["Aşere-i Mübeşşere'den", 'Zâhid sahabi', 'Cesur sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('said-zayd', 'said_bin_zayd_idle.mp4'),
  },
  'umm-fadl': {
    id: 'umm-fadl',
    name: "Ümmü'l-Fadl",
    name_en: 'Umm al-Fadl (RA)',
    name_ar: '',
    name_tr: "Ümmü'l-Fadl (RA)",
    shortName: 'Umm al-Fadl',
    fullName: 'Lubabah bint al-Harith al-Hilaliyyah',
    gender: 'female',
    image: buildImageUrl('umm-fadl', 'umm_fadl.jpg'),
    images: buildResponsiveImageUrls('umm-fadl', 'umm_fadl.jpg'),
    sahabeName: 'ummu_fadl_en',
    heygenAvatarId: '3e8512e36e454e07a41ae5b1f40130dc',
    heygenLiveAvatarId: '10828acd-362b-448d-a2a1-762a4d15266a',
    description:
      "Known as Lubabah bint al-Harith, Umm al-Fadl's birth year is not clearly recorded. She is remembered as the second woman to accept Islam after Khadijah. She was the wife of the Prophet's uncle al-'Abbas and the mother of Ibn 'Abbas, the famed \"Interpreter of the Qur'an.\" Honored with nursing the Prophet's grandson al-Husayn, she was courageous enough to stand up to Abu Lahab and pious enough to keep regular voluntary fasts. She is reported to have died in the era of 'Uthman, before her husband.",
    description_tr:
      "Asıl adı Lübâbe bint Hâris olan ve doğum yılı kaynaklarda net belirtilmeyen bu yüce sahabi, Hz. Hatice'den sonra İslam'ı kabul eden ikinci kadın olma ayrıcalığıyla tanınır. Peygamber Efendimizin (sallallâhu aleyhi ve sellem) amcası Hz. Abbas'ın eşi ve \"ümmetin âlimi\" Abdullah b. Abbas'ın annesidir. Peygamber Efendimizin (sallallâhu aleyhi ve sellem) torunu Hz. Hüseyin'i emzirme şerefine nail olan Ümmü'l-Fadl, Ebu Leheb'in karşısına dikilecek kadar cesur, pazartesi ve perşembe oruçlarını aksatmayacak kadar takva sahibiydi. Resûlullah'ın \"mümine kız kardeşim\" iltifatına mazhar olan bu fedakâr hanım, eşi Hz. Abbas'tan önce, Hz. Osman döneminde vefat ettiği rivayet edilmektedir.",
    description_ar: '',
    era_en: '560~640 CE',
    era_tr: '560~640 MS',
    title: 'Early Muslim Woman',
    title_tr: 'İlk Müslüman Kadın',
    title_ar: '',
    tags: ['From the Pioneer Believers', 'Fearless Female Companion', 'Leader Female Companion'],
    tags_tr: ['Öncü Müminlerden', 'Korkusuz kadın sahabi', 'Lider kadın sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('umm-fadl', 'umm_fadl_idle.mp4'),
  },
  'abbas-muttalib': {
    id: 'abbas-muttalib',
    name: 'Hz. Abbas bin Abdülmuttalib',
    name_en: 'Al-Abbas ibn Abd al-Muttalib (RA)',
    name_ar: '',
    name_tr: 'Hz. Abbas bin Abdülmuttalib (RA)',
    shortName: 'al-Abbas',
    fullName: 'Al-Abbas ibn Abd al-Muttalib ibn Hashim',
    gender: 'male',
    image: buildImageUrl('abbas-muttalib', 'abbas_bin_muttalib.jpg'),
    images: buildResponsiveImageUrls('abbas-muttalib', 'abbas_bin_muttalib.jpg'),
    sahabeName: 'abbas_b_abdilmuttalib_elkuraysi_elhasimi_en',
    heygenAvatarId: '996887adb74f482f826b330d77ffa47b',
    heygenLiveAvatarId: 'a32bfab5-d928-4763-98cc-9841b6fa63e6',
    description:
      'Born about three years before the Messenger of God (peace and blessings be upon him), al-\'Abbas was the Prophet\'s uncle and was honored by the saying, "An uncle is like a second father." Among Quraysh he was known for generosity and strong family loyalty, and he carried out the duty of providing water to pilgrims (siqayah) with openhanded care. He was also known for insight and sound judgment, earning the nickname Dhu al-Ra\'y ("the man of discerning opinion"). In moments of crisis he possessed a powerful voice that could rally scattered troops and restore order. After a life of devotion and support for the Prophet, he died in Medina at around 88 years old (32 AH).',
    description_tr:
      'Peygamber Efendimizden (sallallâhu aleyhi ve sellem) yaklaşık üç yıl önce dünyaya gelen Hz. Abbas, "Amca, baba yerindedir" hadis-i şerifiyle taltif edilen mübarek amcadır. Kureyş içinde cömertliği ve güçlü aile bağlarıyla tanınan Hz. Abbas, hacılara su dağıtma (sikâye) görevini büyük bir özveriyle yerine getirmiştir. Feraseti ve isabetli görüşleriyle "Zü\'r-Re\'y" (görüş sahibi) lakabını kazanan bu büyük zat, kriz anlarında dağılan askerleri bir araya toplayacak güçte bir sese sahipti. Ömrünü Peygamber Efendimize (sallallâhu aleyhi ve sellem) destek ve bağlılıkla geçiren Hz. Abbas, Medine\'de yaklaşık 88 yaşında (Hicri 32) vefat etmiştir.',
    description_ar: '',
    era_en: '560~652 CE',
    era_tr: '560~652 MS',
    title: "Prophet's Uncle",
    title_tr: "Peygamber'in Amcası",
    title_ar: '',
    tags: [
      'Uncle of the Prophet',
      'Possessor of Sound Judgment',
      'Most Generous of Quraysh',
      'Leader',
    ],
    tags_tr: ['Peygamberimizin amcası', 'İsabetli görüş sahibi', "Kureyş'in en cömerdi", 'Lider'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abbas-muttalib', 'abbas_bin_muttalib_idle.mp4'),
  },
  'zayd-harithah': {
    id: 'zayd-harithah',
    name: 'Hz. Zeyd b. Hârise',
    name_en: 'Zayd ibn Harithah (RA)',
    name_ar: '',
    name_tr: 'Hz. Zeyd b. Hârise',
    shortName: 'Zayd',
    fullName: 'Zayd ibn Harithah al-Kalbi',
    gender: 'male',
    image: buildImageUrl('zayd-harithah', 'zayd_bin_harithah.jpg'),
    images: buildResponsiveImageUrls('zayd-harithah', 'zayd_bin_harithah.jpg'),
    sahabeName: 'zeyd_b_harise_en',
    heygenAvatarId: 'b9cfd3f612d64f01a504312f1ef30b86',
    heygenLiveAvatarId: '58fd7902-a0ec-49c4-9d2f-accca24bdbf1',
    description:
      "Originally born free, Zayd was kidnapped and enslaved, then found spiritual elevation in the Prophet's compassionate household. When his father came to reclaim him, Zayd chose the Prophet Muhammad (peace and blessings be upon him) over his biological family—an act of loyalty that earned him the title \"the Beloved of the Messenger of God.\" He holds the unique distinction of being the only Companion explicitly named in the Qur'an. Appointed repeatedly as a military commander, he fought at Mu'tah against Byzantine forces and was martyred while defending the Muslim banner, at about 55 years of age.",
    description_tr:
      "Aslen hür bir çocukken kaçırılıp köleleştirilen, ancak Peygamber Efendimizin (sallallâhu aleyhi ve sellem) şefkatli hanesinde manevi zirvelere ulaşan büyük bir sahabidir. Babası onu geri götürmek istediğinde Peygamber Efendimizi (sallallâhu aleyhi ve sellem) öz ailesine tercih etmesiyle tanınmış, bu eşsiz sadakati sebebiyle \"Resûlullah'ın Sevgilisi\" unvanını almıştır. O, Kur'ân-ı Kerîm'de ismi açıkça zikredilen tek sahabi olma ayrıcalığını taşıyan İslam'ın ilklerindendir. Peygamber Efendimizin (sallallâhu aleyhi ve sellem) pek çok kez ordu komutanı tayin ettiği bu yiğit sancaktar, Mute'de Bizans ordusuna karşı en önde çarpışmıştır. İslam sancağını yere düşürmemek için canını feda ederek şehit olduğunda 55 yaşındaydı.",
    description_ar: '',
    era_en: '620~625 CE',
    era_tr: '620~625 MS',
    title: 'Beloved of the Prophet',
    title_tr: "Peygamber'in Sevgilisi",
    title_ar: '',
    tags: [
      'One of the Most Beloved to the Messenger of God',
      'Meritorious Commander',
      'Loyal Companion',
    ],
    tags_tr: ["Resûlullah'ın en sevdiklerinden", 'Liyakatli Kumandan', 'Vefakâr sahabi'],
    tags_ar: [],
    idleVideo: buildVideoUrl('zayd-harithah', 'zayd_bin_harithah_idle.mp4'),
  },
  'abdullah-jahsh': {
    id: 'abdullah-jahsh',
    name: 'Hz. Abdullah b. Cahş',
    name_en: 'Abdullah ibn Jahsh (RA)',
    name_ar: '',
    name_tr: 'Hz. Abdullah b. Cahş',
    shortName: 'Ibn Jahsh',
    fullName: 'Abdullah ibn Jahsh al-Asadi',
    gender: 'male',
    image: buildImageUrl('abdullah-jahsh', 'abdullah_bin_jahsh.jpg'),
    images: buildResponsiveImageUrls('abdullah-jahsh', 'abdullah_bin_jahsh.jpg'),
    sahabeName: 'abdullah_b_cahs_b_riab_en',
    heygenAvatarId: '0f095263162c44e6b20f75757563e7d9',
    heygenLiveAvatarId: '09c2780a-58f6-47b4-afcf-8cbdd34251ba',
    description:
      'A cousin and foster-brother of the Prophet (peace and blessings be upon him), Ibn Jahsh was among the earliest emigrants—making both the Abyssinian and Medinan migrations. He is described as the first to be given a banner in a military detachment and is sometimes remembered as an early commander greeted with the title "leader of the believers" in that context. His most striking feature was the prayer he made before Uhud—asking to be granted the sacrifice of his limbs in God\'s path. His supplication was answered; he was martyred at Uhud and later remembered with the epithet al-Mujadda\' fillah ("the one mutilated for God"). He was buried in the same grave as his uncle Hamzah and was in his 40s.',
    description_tr:
      'Peygamber Efendimizin (sallallâhu aleyhi ve sellem) hem halasının oğlu hem de sütkardeşi olan bu yiğit sahabi, İslam tarihinde kendisine ilk sancak verilen ve görevlendirildiği seriyye sebebiyle "Emîru\'l-müminîn" unvanıyla selamlanan ilk ordu komutanıdır. Hem Habeşistan\'a hem de Medine\'ye hicret eden ilklerden olup, inancı uğruna büyük fedakârlıklar göstermiştir. Onun en dikkat çekici özelliği ve alamet-i farikası ise Uhud Gazvesi öncesinde "Allah yolunda uzuvlarını feda etmeyi" dileyerek yaptığı duadır. Bu duası kabul olunmuş ve şehadetinden sonra kendisine "el-Müceddâ\' fi\'llâh" (Allah yolunda kulağı kesilen) unvanı verilmiştir. Kırk küsur yaşında Uhud\'da şehit düşmüş ve dayısı Hz. Hamza ile aynı kabre defnedilmiştir.',
    description_ar: '',
    era_en: '580~625 CE',
    era_tr: '580~625 MS',
    title: 'Martyr',
    title_tr: 'Şehit',
    title_ar: '',
    tags: [
      'Valiant and Selfless Companion',
      'Foster Brother of the Prophet',
      'The First Commander',
    ],
    tags_tr: ['Yiğit ve fedakâr sahabi', 'Peygamberimizin Sütkardeşi', 'İlk Komutan'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abdullah-jahsh', 'abdullah_bin_jahsh_idle.mp4'),
  },
  'fatimah-asad': {
    id: 'fatimah-asad',
    name: 'Hz. Fatıma bint Esed',
    name_en: 'Fatimah bint Asad (RA)',
    name_ar: '',
    name_tr: 'Hz. Fatıma bint Esed',
    shortName: 'Fatimah',
    fullName: 'Fatimah bint Asad ibn Hashim',
    gender: 'female',
    image: buildImageUrl('fatimah-asad', 'fatimah_bint_asad.jpg'),
    images: buildResponsiveImageUrls('fatimah-asad', 'fatimah_bint_asad.jpg'),
    sahabeName: 'fatima_bint_esed_en',
    heygenAvatarId: '075e17bad98e4f63b30e0dc2dc9b7f85',
    heygenLiveAvatarId: 'ddd1e145-6c82-4df3-9124-27808529877f',
    description:
      "The wife of Abu Talib and mother of 'Ali, Fatimah bint Asad is remembered for extraordinary compassion. After the Messenger of God (peace and blessings be upon him) was orphaned, she cared for him as her own—feeding him even when her children went hungry. For this, the Prophet honored her with the words: \"My mother after my mother.\" She was among the early women to emigrate to Medina, and she held a special place in the Prophet's heart. When she died, he shrouded her in his own garment and entered her grave to make a personal supplication. She passed away in Medina around 625 CE and was buried in al-Baqi'.",
    description_tr:
      "Peygamber Efendimizin (sallallâhu aleyhi ve sellem) amcası Ebu Talib'in eşi ve Hz. Ali'nin annesi olan bu mübarek hanım, şefkatiyle tarihe geçmiştir. Yetim kalan küçük Muhammed'i (sallallâhu aleyhi ve sellem) öz çocuğu gibi bağrına basmış, kendi çocukları aç kalsa bile onu doyurmuştur. Bu fedakârlığı sebebiyle Efendimiz (sallallâhu aleyhi ve sellem) kendisini \"Annemden sonraki annem\" diye şereflendirmiştir. Medine'ye hicret eden ilk hanımlardan olan Fâtıma bint Esed, vefat ettiğinde Resûlullah (sallallâhu aleyhi ve sellem) onu kendi gömleğiyle kefenlemiş ve kabrine bizzat inerek dua etmiştir. Miladi 625 yılında Medine'de vefat etmiş ve Bakî Kabristanı'na defnedilmiştir.",
    description_ar: '',
    era_en: '570~625 CE',
    era_tr: '570~625 MS',
    title: 'Mother of Ali',
    title_tr: "Ali'nin Annesi",
    title_ar: '',
    tags: ['Second Mother', 'Pioneer Emigrant', "Mother of 'Ali"],
    tags_tr: ['İkinci Anne', 'Öncü Muhacir', "Hz. Ali'nin annesi"],
    tags_ar: [],
    idleVideo: buildVideoUrl('fatimah-asad', 'fatimah_bint_asad_idle.mp4'),
  },
  'abdullah-salam': {
    id: 'abdullah-salam',
    name: 'Hz. Abdullah b. Selâm',
    name_en: 'Abdullah ibn Salam (RA)',
    name_ar: '',
    name_tr: 'Hz. Abdullah b. Selâm (RA)',
    shortName: 'Ibn Salam',
    fullName: 'Abdullah ibn Salam ibn al-Harith',
    gender: 'male',
    image: buildImageUrl('abdullah-salam', 'abdullah_bin_salam.jpg'),
    images: buildResponsiveImageUrls('abdullah-salam', 'abdullah_bin_salam.jpg'),
    sahabeName: 'abdullah_b_selam_en',
    heygenAvatarId: '3791efce035041999ecdd40ff05d46da',
    heygenLiveAvatarId: '65de2175-d6d6-4749-91a5-38c4c7490e34',
    description:
      "A great Jewish scholar of Medina, 'Abdullah ibn Salam recognized the Messenger of God (peace and blessings be upon him) upon his arrival—identifying him through descriptions he knew from the Torah—and embraced Islam immediately. His original name was Husayn, but the Prophet gave him the name 'Abdullah. The Qur'an praises him as \"a witness from among the Children of Israel.\" Promised Paradise while still alive, he was admired for knowledge, humility, and a unifying spirit. During times of turmoil, he urged calm and brotherhood, embodying the dignity of scholarship. He died in Medina in 43 AH (663 CE).",
    description_tr:
      "Peygamber Efendimizin (sallallâhu aleyhi ve sellem) Medine'ye teşrifiyle birlikte, O'nu Tevrat'taki vasıflarından tanıyarak hemen iman eden ve \"İsrailoğullarından bir şahit\" olarak Kur'an'da övülen büyük bir âlimdir. Asıl adı Husayn iken, bu güzel ismi kendisine bizzat Efendimiz vermiştir. O, eski kutsal kitaplara olan derin vukufiyetiyle Hakk'ı tereddütsüz tasdik etmiştir. Yaşarken cennetle müjdelenen bu mübarek sahabi, ilmi ve tevazusuyla gönüllerde taht kurmuştur. Her daim birleştirici olmuş, fitne zamanlarında Müslümanları sükûnete ve kardeşliğe davet ederek ilmin vakarını yansıtmıştır. Hicri 43 (Miladi 663) yılında Medine'de vefat etmiştir.",
    description_ar: '',
    era_en: '570~663 CE',
    era_tr: '570~663 MS',
    title: 'Jewish Scholar',
    title_tr: 'Yahudi Alimi',
    title_ar: '',
    tags: ['A Witness From the Children of Israel', 'Great Scholar', 'Man of Peace'],
    tags_tr: ['İsrailoğullarından bir şahit', 'Büyük alim', 'Sulh Ehli'],
    tags_ar: [],
    idleVideo: buildVideoUrl('abdullah-salam', 'abdullah_bin_salam_idle.mp4'),
  },
};

/**
 * Get avatar info by ID
 */
export function getAvatarInfo(avatarId: string): AvatarInfo | undefined {
  return AVATARS[avatarId];
}

/**
 * Get all available avatars
 */
export function getAllAvatars(): AvatarInfo[] {
  return Object.values(AVATARS);
}

/**
 * Get sahabe name for RAG API
 */
export function getSahabeNameForAvatar(avatarId: string): string | undefined {
  return AVATARS[avatarId]?.sahabeName;
}

/**
 * Check if avatar exists
 */
export function avatarExists(avatarId: string): boolean {
  return avatarId in AVATARS;
}

/**
 * Get HeyGen avatar ID for streaming
 * Falls back to default (Bilal) if not specified
 */
export function getHeyGenAvatarId(avatarId: string): string {
  const avatar = AVATARS[avatarId];
  return avatar?.heygenLiveAvatarId || avatar?.heygenAvatarId || DEFAULT_HEYGEN_AVATAR_ID;
}

/**
 * Get avatar video assets (intro and idle)
 * Falls back to avatar's idle video for intro if no intro specified
 * Falls back to Bilal's idle video as ultimate default
 */
export function getAvatarVideos(avatarId: string): { intro: string; idle: string } {
  const avatar = AVATARS[avatarId];
  const idle = avatar?.idleVideo ?? bilalIdleVideo;
  return {
    intro: avatar?.introVideo ?? idle,
    idle,
  };
}
